import {
  createIdGenerator,
  type ModelMessage,
  type UIMessage,
  type ToolUIPart,
} from 'ai';
import { llmInputBuilder } from '../llm/input-builder';
import { modelAdapterFor } from '../llm/model-input-adapter';
import type {
  ComprehensionEncoding,
  Fact,
  IngotSchema,
  LLMInput,
  LLMInputBuilder,
  LlmCallSettings,
  ParticipantPromptContext,
  Receipt,
} from '../llm/llm-input-types';
import {
  toLlmConversationPromptOptions,
  type ConversationPromptOptions,
} from './conversation-prompt-source';
import {
  ConversationRenderer,
  shouldInjectLearnedFactsForPrompt,
  shouldInjectRegistrySkillsForPrompt,
} from './conversation-renderer';
import type { SerializedConversation } from './serialized-conversation';
import type { IngotToolReceipt } from './ingot-receipt';
import { omit, uniqBy } from 'lodash';
import objectHash from 'object-hash';
import { ToolKey } from '../tool-key';
import {
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_INGOT_QUERY_TOOL_KEY,
  COCO_DATASET_READ_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_SEARCH_TOOL_KEY,
  COCO_SITE_SCRAPER_TOOL_KEY,
  COCO_WEB_SEARCH_TOOL_KEY,
  DEFAULT_COCO_TOOL_KEYS,
  agentIdToAgentToolKey,
  isAgentToolKey,
  workflowIdToWorkflowToolKey,
} from './coco-tools';
import {
  ChatData,
  ChatModelData,
  ChatTriggerSource,
  ChatActionData,
  ChatActionType,
  ChatActionStatus,
  ToolLoader,
  CocoToolKeys,
  ArtifactDescriptor,
  ArtifactOrigin,
  AgentLoader,
  ChatLoadedResourceType,
  LoadedResourceDescriptor,
  ResolvedSkill,
  InterruptableToolDefinition,
  INTERRUPTABLE_TOOL_KEY_PREFIX,
} from './types';
import {
  AssistantMessage,
  Message as MessageClass,
  SystemMessageClass,
  ToolPart,
  type StoredSystemMessagePayload,
} from './message';
import {
  createToolPart,
  rawMessagePartIsImage,
  uiMessageHasImageParts,
  uiMessageHasToolImageParts,
} from './message-parts';
import { incomplete } from './tool-incomplete';
import { ChatModel } from './chat-model';
import type { ChatSentimentLogRecord } from './chat-sentiment-log';
import {
  applyReceiptsToMessages,
  buildCompactionsFromMessages,
  buildCountsFromTurns,
  buildTurnsFromMessages,
  foldSentimentLogRecords,
  mapFactsForInsights,
  serializeConversationResult,
  type ConversationInsights,
} from './conversation-insights';
import { buildLlmUsageByMessageId } from './assistant-message-llm-usage';
import {
  buildReferencedDocumentsEditorStateMap,
  collectEditorStateRootsFromMessages,
  type WorkspaceDomainLoader,
} from './workspace-prefetch';
import { BuildExplorer } from '../dg/commit/build-explorer';
import type { DocumentNode, Resolution } from '../dg/graph/types';
import { Identity } from '../dg/graph/types';
import type { DocumentState } from '../dg/document/document-types';
import { EditorState } from '../dg/document/editor';
import {
  collectWorkspaceResourceRefs,
  type WorkspaceResourceRef,
} from '../dg/document/collect-workspace-resource-refs';
import {
  ChatAction,
  ToolApprovalAction,
  MessageQueueAction,
  AddToolAction,
  RemoveToolAction,
  LoadResourceAction,
  LoadSkillAction,
  UnloadResourceAction,
  LoadArtifactAction,
  RemoveArtifactAction,
  FocusAgentAction,
  UnfocusAgentAction,
  OutputShapeAction,
  AutoApproveAction,
  RemoveAutoApproveAction,
  EnterPlanModeAction,
  ExitPlanModeAction,
  InterruptedToolDataProvidedAction,
  InterruptedToolInputAction,
  RegisterInterruptableToolAction,
} from './chat-action';
import { coco, ResourceType } from '@cotera/api';
import { Chat } from './chat';
import type { Agent } from './agent';
import type { AssistantMessageLlmUsage } from './assistant-message-llm-usage';
import { Lazy } from './lazy';
import type {
  JsonValue,
  Serializable,
  SerializableClass,
} from '../serializable';
import { Assert } from '@cotera/utilities';
import { SYSTEM_USER_USER_ID } from '@cotera/utilities';
import { RunKeyHelpers } from '../atc';
import { EraQL, Ty } from '@cotera/era';
import type { LoadedAgentContent } from './types';

const generateMessageId = createIdGenerator({ prefix: 'msg', size: 16 });
const generateToolCallId = createIdGenerator({ prefix: 'call', size: 16 });
const generateApprovalId = createIdGenerator({ prefix: 'approval', size: 16 });
const DOCUMENT_REFERENCE_ID_PATTERN =
  /\(document:\s*([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\)/gi;

type JsonSerializableValue =
  | string
  | number
  | boolean
  | null
  | JsonSerializableValue[]
  | { [key: string]: JsonSerializableValue };

function normalizeToJsonSerializable(
  value: unknown
): JsonSerializableValue | undefined {
  if (value === null) {
    return null;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    typeof value === 'number'
  ) {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      return null;
    }
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      const normalizedItem = normalizeToJsonSerializable(item);
      return normalizedItem === undefined ? null : normalizedItem;
    });
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const normalizedRecord: Record<string, JsonSerializableValue> = {};
    for (const [key, recordValue] of Object.entries(record)) {
      const normalizedValue = normalizeToJsonSerializable(recordValue);
      if (normalizedValue !== undefined) {
        normalizedRecord[key] = normalizedValue;
      }
    }
    return normalizedRecord;
  }

  return undefined;
}

export type Message = {
  uiMessage: UIMessage;
  type: 'chat' | 'compaction' | 'hidden';
  /** For assistant messages only: when the message was read. null = unread. */
  readAt?: Date | null;
  createdAt?: Date;
  updatedAt?: Date;
  /** 'streaming' = incomplete (e.g. process crashed mid-stream); excluded from getModelMessages. */
  status?: 'ready' | 'streaming' | null;
  llmUsage?: AssistantMessageLlmUsage | null;
  /**
   * User (actor) who authored this row (`chat_messages.actor_id`). Threaded
   * through so user turns can be labeled `From: <name>` in group chats.
   */
  actorId?: string;
};

/** Params for addArtifact(); matches LoadArtifactAction.create input (minus orgId/chatId). */
export type AddArtifactParams = {
  artifactType: string;
  artifactId?: string;
  data?: unknown;
  toolCallId?: string;
};

/** Artifacts to show when a chat is created (e.g. agent when navigating from agent page). */
export type InitialArtifactParams = AddArtifactParams;

/** Actions to apply when a chat is created (e.g. focus_agent when navigating from agent page). */
export type InitialAction =
  | { type: 'focus_agent'; agentId: string }
  | { type: 'unfocus_agent' }
  | { type: 'output_shape'; shape: Ty.ExtendedAttributeType }
  | { type: 'auto_approve' }
  | { type: 'remove_auto_approve' };

/**
 * Minimal shape for sub-conversation hydration (typically {@link Conversation}).
 * Structural typing avoids forward-reference issues on {@link ConversationParams}.
 */
export type ChildConversationForArtifacts = {
  artifacts(): Promise<ArtifactDescriptor[]>;
  /** Optional sync fingerprint when the child is a hydrated {@link Conversation}. */
  artifactPanelSyncKey?(): string;
};

export type ConversationParams = {
  orgId: string;
  conversationId: string;
  /** Defaults to coco.SYSTEM_MESSAGE when omitted. */
  systemMessage?: string;
  messages: Message[];
  chat?: ChatData;
  /**
   * Hydrated saved agent when `chat.agentId` links to the agents table.
   * Populated by server loaders; omitted for client-only / serialized replay.
   */
  linkedAgent?: Agent;
  /**
   * Parent thread when this chat is a sub-agent (`chats.parent_id`). Pass
   * {@link Conversation.lazyParent} with a loader that hydrates the parent **without**
   * listing this chat in {@link ConversationParams.children} (avoids cycles), or
   * {@link Lazy.resolved} when the parent is already materialized.
   */
  parent?: Lazy<Conversation | null>;
  /** Latest model switch metadata for this chat (loaded on demand). */
  model?: Lazy<ChatModel | null>;
  /**
   * Latest `chat_sentiment_log` rows for this chat (loaded on demand).
   * Pass {@link Conversation.lazySentimentLog} with a loader that returns hydrated
   * {@link ChatSentimentLogRecord} rows (typically the latest analysis batch).
   */
  sentimentLog?: Lazy<readonly ChatSentimentLogRecord[]>;
  /**
   * Sub-agent chats (`chats.parent_id` = this conversation), hydrated recursively.
   * When non-empty, {@link Conversation.artifacts} merges each child’s resolved
   * `artifacts()` (awaited)
   * after this chat’s `load_artifact` actions and tool message outputs, so child artifacts
   * override the parent on duplicate `artifactId` (see {@link Conversation.artifacts}).
   */
  children?: ChildConversationForArtifacts[];
  loadedTools?: ToolKey[];
  messagePositions?: Map<string, number>;
  actions?: ChatActionData[];
  /**
   * High-level actions to apply on first construction (e.g. focus_agent when opening a chat from an agent).
   * Each becomes a new persisted action via {@link Conversation.focusAgent} / {@link Conversation.unfocusAgent} /
   * {@link Conversation.setOutputShape}.
   * Omit when hydrating from the database (use {@link ConversationParams.actions} instead).
   */
  initialActions?: InitialAction[];
  /**
   * Ids in {@link ConversationParams.actions} that are in-memory only and have
   * not been written to the database yet. Used by {@link Conversation.fromSerialized}
   * to preserve dirty-tracking across a serialize/deserialize boundary (e.g.
   * when an `initializeChat` durable is split in two). Action ids listed here
   * skip {@link Conversation.initialActionIds} and instead populate
   * {@link Conversation.newActionIds} via the normal tracking loop, so the
   * next save still persists them.
   */
  unpersistedActionIds?: ReadonlySet<string>;
  toolLoader?: ToolLoader;
  /** Loads agent instructions for agent tool keys in the skills block. */
  agentLoader?: AgentLoader;
  /** Resolves developer-defined skills for supplemental LLM prompt blocks. */
  skillLoader?: SkillLoader;
  workspaceDomainLoader?: WorkspaceDomainLoader;
  cocoToolKeys?: CocoToolKeys;
  /**
   * Document id → TipTap JSON for workspace documents referenced from chat messages.
   * When set, {@link Conversation.getLoadedTools} also collects tools (and agent tool keys)
   * from @mentions inside those documents, including nested document bodies (see
   * {@link buildReferencedDocumentsEditorStateMap}).
   * When omitted, {@link Conversation.fromChatMessagesAsync} fills this using
   * {@link ConversationParams.workspaceDomainLoader} and `editor-state` message parts.
   */
  referencedDocumentsById?: ReadonlyMap<string, DocumentState>;
  /** Map of assistant message ID to read timestamp. null = unread. */
  messageReadAt?: Map<string, Date | null>;
  /** Optional suffix scanned for document references in skills. */
  skillsSuffix?: string;
  /**
   * Loads durable user/org facts for {@link Conversation.facts}. Typically
   * wired by the server chat repository; omitted for tests and non-Coco chats.
   */
  learnedFactsLoader?: LearnedFactsLoader;
  /**
   * Loads {@link Receipt}s for previously-completed tool calls in this chat
   * (see {@link Conversation.toolReceipts}). Bound to a specific chat by the
   * chat repository; omitted for tests and non-Coco chats.
   */
  toolReceiptsLoader?: ToolReceiptsLoader;
  /**
   * Loads the chat's ingot schema for {@link Conversation.ingotSchema}. Bound
   * to a specific chat by the chat repository; omitted for tests and non-Coco
   * chats.
   */
  ingotSchemaLoader?: IngotSchemaLoader;
  /**
   * Precomputed "an organize-library draft row exists for this conversation"
   * flag. Wired in by the server turn handler (which has db + ids in scope) so
   * the synchronous {@link Conversation.hasOrganizeDraft} can back the organize
   * skill's gate even when the one-shot button seed was lost. Omitted for tests
   * and non-Coco chats.
   */
  organizeDraftExists?: boolean;
};

/**
 * Loads the tables in the chat's ingot. Returns null when they can't be read
 * this turn; the prompt then goes without the schema block.
 */
export type IngotSchemaLoader = () => Promise<IngotSchema | null>;

/** Loads learned facts for a given actor (see {@link Conversation.facts}). */
export type LearnedFactsLoader = (actorId: string) => Promise<readonly Fact[]>;

/**
 * Loads stored {@link Receipt}s for the chat this loader was constructed for.
 * The renderer uses these to substitute durable summaries for raw tool
 * outputs.
 */
export type ToolReceiptsLoader = () => Promise<readonly Receipt[]>;

/** Loads developer-defined skills for loaded skill keys (server-side only). */
export type SkillLoader = {
  getActiveSkillKeys: (
    conversation: Conversation
  ) => Promise<readonly string[]>;
  loadSkills: (
    skillKeys: readonly string[],
    conversation: Conversation
  ) => Promise<ResolvedSkill[]>;
};

/**
 * Tool layer sometimes completes with a single `{ error: string }` payload (e.g. bad-request
 * paths that return an error object instead of throwing). That must be stored as
 * {@link ToolUIPart.state} `output-error`, aligned with the chat UI "tool error" callout.
 */
function isToolFailureErrorEnvelopeResult(result: unknown): boolean {
  if (typeof result !== 'object' || result === null) {
    return false;
  }
  const keys = Object.keys(result as Record<string, unknown>);
  if (keys.length !== 1 || keys[0] !== 'error') {
    return false;
  }
  const err = (result as { error?: unknown }).error;
  return typeof err === 'string' && err.length > 0;
}

/** Adds the Ingot receipt under `cotera`, keeping whatever metadata the part already had. */
function withIngotReceipt(
  metadata: Record<string, unknown> | undefined,
  ingot: IngotToolReceipt
): Record<string, unknown> {
  const existing = metadata?.['cotera'];
  const cotera =
    typeof existing === 'object' &&
    existing !== null &&
    !Array.isArray(existing)
      ? existing
      : {};
  return { ...metadata, cotera: { ...cotera, ingot } };
}

export type ConversationRendererOptions = {
  includeDefaults?: boolean;
  /** When false, skip registry skill injection for this prompt (e.g. extract-object). */
  injectSkills?: boolean;
  actorId: string;
  prune?: (message: MessageClass, index: number) => boolean;
  /**
   * Set for group chats (>1 human participant) so user turns are labeled
   * `From: <name>` and a multi-participant system note is added. Omit for 1:1
   * chats (the common case) to leave the prompt unchanged.
   */
  participantContext?: ParticipantPromptContext;
};

function isNestedSubAgentParentId(
  parentId: string | null | undefined
): boolean {
  return parentId !== null && parentId !== undefined && parentId !== '';
}

/**
 * Latest `focus_agent` target from raw params — persisted {@link
 * ConversationParams.actions} plus {@link ConversationParams.initialActions},
 * which are applied afterwards and therefore win. Mirrors
 * {@link Conversation.getFocusedAgentId} for the pre-construction check in
 * {@link Conversation.createFromParams}.
 */
function focusedAgentIdFromParams(params: ConversationParams): string | null {
  const focusActions = (params.actions ?? [])
    .map((action, index) => ({ action, index }))
    .filter(
      ({ action }) =>
        action.type === ChatActionType.FocusAgent ||
        action.type === ChatActionType.UnfocusAgent
    )
    .sort(
      (a, b) =>
        b.action.createdAt.getTime() - a.action.createdAt.getTime() ||
        b.index - a.index
    );

  let focused: string | null = null;
  const mostRecent = focusActions[0]?.action;
  if (
    mostRecent !== undefined &&
    mostRecent.type === ChatActionType.FocusAgent
  ) {
    const agentId = mostRecent.data?.['agentId'];
    focused = typeof agentId === 'string' && agentId !== '' ? agentId : null;
  }

  for (const initialAction of params.initialActions ?? []) {
    if (initialAction.type === 'focus_agent') {
      focused = initialAction.agentId;
    } else if (initialAction.type === 'unfocus_agent') {
      focused = null;
    }
  }

  return focused;
}

/**
 * Whether this chat runs *as* a saved agent (agent prompt + agent tool rules).
 *
 * An `agent_chats` link alone is not enough: persisting a `focus_agent` action
 * links the focused agent to the chat, so agent-editor chats carry an `agentId`
 * while still being Coco chats that invoke that agent as a sub-agent tool.
 */
function hasLinkedSavedAgent(params: ConversationParams): boolean {
  const linkedAgentId = params.linkedAgent?.id ?? params.chat?.agentId ?? null;
  if (linkedAgentId === null || linkedAgentId === '') {
    return false;
  }
  return focusedAgentIdFromParams(params) !== linkedAgentId;
}

export type ConversationToLlmInputOptions = ConversationRendererOptions & {
  /** Override {@link resolveLlmSystemMessage} (tests / rare callers). */
  system?: SystemMessageClass;
  comprehensionEncoding?: ComprehensionEncoding;
  /** Sampling and provider options (e.g. from server `transformGenerateParams`). */
  callSettings?: LlmCallSettings;
  /** streamText `output` (e.g. single-pass structured output). */
  streamTextOutput?: LLMInput['output'];
  /**
   * Era output type for single-pass structured output. Resolved in
   * {@link LLMInputBuilder.model} (Zod for Google, JSON Schema for other providers).
   */
  outputShape?: Ty.ExtendedAttributeType;
};

function appendUniqueToolKeys(
  result: string[],
  keys: readonly string[],
  options?: { excluded?: ReadonlySet<string> }
): string[] {
  const out = [...result];
  const seen = new Set(out);
  for (const key of keys) {
    if (options?.excluded?.has(key) === true) {
      continue;
    }
    if (!seen.has(key)) {
      out.push(key);
      seen.add(key);
    }
  }
  return out;
}

/**
 * Domain model for a conversation.
 * Manages messages, tool loading, and provides methods for
 * cleaning and pruning messages for LLM consumption.
 */
export abstract class Conversation
  implements Serializable<SerializedConversation>
{
  private readonly orgId: string;
  private readonly conversationId: string;
  private readonly systemMessage: string;
  private messages: MessageClass[];
  private readonly includedTools: ToolKey[];
  private loadedTools: ToolKey[];
  private actions: ChatAction[];
  private readonly chatModel?: Chat;
  private readonly initialMessageIds: Set<string>;
  private modifiedMessageIds: Set<string> = new Set();
  private newMessageIds: Set<string> = new Set();
  private readonly initialActionIds: Set<string>;
  private modifiedActionIds: Set<string> = new Set();
  private newActionIds: Set<string> = new Set();
  private readonly messagePositions: Map<string, number>;
  private readonly toolLoader?: ToolLoader;
  private readonly agentLoader?: AgentLoader;
  private readonly skillLoader?: SkillLoader;
  private readonly workspaceDomainLoader?: WorkspaceDomainLoader;
  private readonly referencedDocumentsById?:
    | ReadonlyMap<string, DocumentState>
    | undefined;
  private readonly cocoToolKeys: CocoToolKeys;
  private readonly messageReadAt: Map<string, Date | null>;
  private readonly linkedAgent?: Agent;
  private readonly children: ChildConversationForArtifacts[];
  private readonly parentConversation?: Lazy<Conversation | null>;
  private readonly conversationModel?: Lazy<ChatModel | null>;
  private readonly chatSentimentLog?: Lazy<readonly ChatSentimentLogRecord[]>;
  private readonly skillsSuffix?: string;
  private readonly learnedFactsLoader?: LearnedFactsLoader;
  private readonly toolReceiptsLoader?: ToolReceiptsLoader;
  private readonly ingotSchemaLoader?: IngotSchemaLoader;
  private readonly organizeDraftExists?: boolean;
  /** Set during {@link toLlmInput} so autoload skills can mirror prompt-build gates. */
  private promptRenderOptions?: ConversationRendererOptions;

  protected constructor(params: ConversationParams) {
    this.orgId = params.orgId;
    this.conversationId = params.conversationId;
    this.systemMessage = params.systemMessage ?? coco.SYSTEM_MESSAGE;
    this.messagePositions = params.messagePositions ?? new Map();
    this.messageReadAt = this.buildMessageReadAtMap(params.messages);
    // Convert ChatActionData[] to ChatAction[] using factory
    // Create actions first so they can be passed to messages
    this.actions = (params.actions ?? []).map((action) =>
      ChatAction.fromData(action)
    );
    // Convert Message[] to MessageClass[] using factory method
    // Pass actions to each message so tool parts can check approval status
    this.messages = params.messages.map((msg) =>
      MessageClass.fromUIMessage(
        this.conversationId,
        msg.uiMessage,
        msg.type,
        this.actions,
        {
          createdAt: msg.createdAt,
          updatedAt: msg.updatedAt,
          status: msg.status === 'streaming' ? 'streaming' : 'ready',
          llmUsage: msg.llmUsage ?? null,
          actorId: msg.actorId,
        }
      )
    );
    this.chatModel = params.chat ? new Chat(params.chat) : undefined;
    this.linkedAgent = params.linkedAgent;
    this.parentConversation = params.parent;
    this.conversationModel = params.model;
    this.chatSentimentLog = params.sentimentLog;
    this.skillsSuffix = params.skillsSuffix;
    this.learnedFactsLoader = params.learnedFactsLoader;
    this.toolReceiptsLoader = params.toolReceiptsLoader;
    this.ingotSchemaLoader = params.ingotSchemaLoader;
    this.organizeDraftExists = params.organizeDraftExists;
    this.children = params.children ?? [];
    this.toolLoader = params.toolLoader;
    this.agentLoader = params.agentLoader;
    this.skillLoader = params.skillLoader;
    this.workspaceDomainLoader = params.workspaceDomainLoader;
    this.referencedDocumentsById = params.referencedDocumentsById;
    this.cocoToolKeys = params.cocoToolKeys ?? DEFAULT_COCO_TOOL_KEYS;
    this.initialMessageIds = new Set(
      params.messages
        .map((msg) => msg.uiMessage.id)
        .filter((id): id is string => id !== undefined)
    );

    const unpersistedActionIds =
      params.unpersistedActionIds ?? new Set<string>();
    this.initialActionIds = new Set(
      params.actions
        ?.map((action) => action.id)
        .filter(
          (id): id is string =>
            id !== undefined && !unpersistedActionIds.has(id)
        ) ?? []
    );

    const providedToolKeys = new Set(
      (params.loadedTools ?? []).map((tool) => tool.toString())
    );
    this.includedTools = Array.from(providedToolKeys).map((toolKey) =>
      ToolKey.fromString(toolKey)
    );
    // Persist only explicitly provided tools. History/action inference is layered
    // on demand by getLoadedTools() via resolveLoadedToolsMergedWithHistory().
    this.loadedTools = this.filterRemovedTools(providedToolKeys);

    // Track which messages are new vs modified
    for (const msgModel of this.messages) {
      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }
      if (this.initialMessageIds.has(msgId)) {
        // This message existed initially, so it's potentially modified
        // We'll mark it as modified when it actually changes
      } else {
        this.newMessageIds.add(msgId);
      }
    }

    // Track which actions are new vs modified
    for (const action of this.actions) {
      if (this.initialActionIds.has(action.id)) {
        // This action existed initially, so it's potentially modified
        // We'll mark it as modified when it actually changes
      } else {
        this.newActionIds.add(action.id);
      }
    }

    for (const initialAction of params.initialActions ?? []) {
      switch (initialAction.type) {
        case 'focus_agent':
          this.focusAgent(initialAction.agentId);
          break;
        case 'unfocus_agent':
          this.unfocusAgent();
          break;
        case 'output_shape':
          this.setOutputShape(initialAction.shape);
          break;
        case 'auto_approve':
          this.autoApprove();
          break;
        case 'remove_auto_approve':
          this.removeAutoApprove();
          break;
        default:
          Assert.unreachable(initialAction);
      }
    }

    this.selfHealInterruptedAssistantFlows();
  }

  /**
   * Create a new Conversation from chat rows / UI messages (synchronous).
   * Nested document tool inference requires {@link ConversationParams.referencedDocumentsById},
   * or use {@link Conversation.fromChatMessagesAsync} when you only have
   * {@link ConversationParams.workspaceDomainLoader}.
   */
  static fromChatMessages(params: ConversationParams): Conversation {
    return Conversation.createFromParams(params);
  }

  /** Picks {@link CocoChatConversation}, {@link SubAgentConversation}, etc. from chat metadata. */
  static createFromParams(params: ConversationParams): Conversation {
    const parentId = params.chat?.parentId ?? null;
    if (isNestedSubAgentParentId(parentId)) {
      return new SubAgentConversation(params);
    }

    const trigger = params.chat?.triggerSource ?? 'chat';
    if (trigger === 'chat' || trigger === 'api') {
      // API-initiated chats are ordinary assistant conversations (just started
      // programmatically), so they behave like `chat` — agent-linked or Coco.
      if (hasLinkedSavedAgent(params)) {
        return new AgentChatConversation(params);
      }
      return new CocoChatConversation(params);
    }
    if (trigger === 'chat-bot') {
      if (hasLinkedSavedAgent(params)) {
        return new ChatBotAgentChatConversation(params);
      }
      return new ChatBotConversation(params);
    }
    if (trigger === 'slack') {
      if (hasLinkedSavedAgent(params)) {
        return new SlackAgentChatConversation(params);
      }
      return new SlackConversation(params);
    }
    if (trigger === 'linear') {
      if (hasLinkedSavedAgent(params)) {
        return new LinearAgentChatConversation(params);
      }
      return new LinearConversation(params);
    }
    if (trigger === 'cron') {
      return new CronConversation(params);
    }
    if (trigger === 'webhook') {
      return new WebhookConversation(params);
    }
    if (trigger === 'event') {
      return new EventConversation(params);
    }
    if (trigger === 'dataset') {
      return new DatasetConversation(params);
    }
    if (trigger === 'warehouse') {
      return new WarehouseConversation(params);
    }
    if (trigger === 'workflow') {
      return new WorkflowConversation(params);
    }
    if (trigger === 'system') {
      return new SystemConversation(params);
    }
    if (trigger === 'compaction-summary') {
      return new CompactionSummaryConversation(params);
    }
    return Assert.unreachable(trigger);
  }

  /**
   * Like {@link Conversation.fromChatMessages}, but when {@link ConversationParams.workspaceDomainLoader}
   * is set and {@link ConversationParams.referencedDocumentsById} is omitted, loads every workspace
   * document reachable from `editor-state` roots so {@link Conversation.getLoadedTools} includes
   * tools @-mentioned inside nested documents.
   */
  static async fromChatMessagesAsync(
    params: ConversationParams
  ): Promise<Conversation> {
    let referencedDocumentsById = params.referencedDocumentsById;
    if (
      referencedDocumentsById === undefined &&
      params.workspaceDomainLoader !== undefined
    ) {
      const roots = [
        ...collectEditorStateRootsFromMessages(params.messages),
        ...(params.linkedAgent !== undefined
          ? [params.linkedAgent.editorState.getState()]
          : []),
      ];
      if (roots.length > 0) {
        referencedDocumentsById = await buildReferencedDocumentsEditorStateMap(
          roots,
          params.workspaceDomainLoader
        );
      }
    }
    return Conversation.createFromParams({
      ...params,
      ...(referencedDocumentsById !== undefined &&
        referencedDocumentsById.size > 0 && {
          referencedDocumentsById,
        }),
    });
  }

  static empty(
    params: Pick<ConversationParams, 'orgId' | 'conversationId'>
  ): Conversation {
    return Conversation.createFromParams({
      orgId: params.orgId,
      conversationId: params.conversationId,
      systemMessage: coco.SYSTEM_MESSAGE,
      messages: [],
      chat: undefined,
      loadedTools: [],
      messagePositions: new Map(),
      actions: [],
      toolLoader: undefined,
      workspaceDomainLoader: undefined,
      cocoToolKeys: DEFAULT_COCO_TOOL_KEYS,
      messageReadAt: new Map(),
      children: [],
    });
  }

  /**
   * Deferred parent thread (`chats.parent_id`), same pattern as {@link Folder.parent}.
   */
  static lazyParent(
    loadParent: () => Promise<Conversation | null>
  ): Lazy<Conversation | null> {
    return new Lazy(loadParent);
  }

  /**
   * Deferred latest model metadata for a chat.
   */
  static lazyModel(
    loadModel: () => Promise<ChatModel | null>
  ): Lazy<ChatModel | null> {
    return new Lazy(loadModel);
  }

  /**
   * Deferred `chat_sentiment_log` rows for a chat.
   */
  static lazySentimentLog(
    loadSentimentLog: () => Promise<readonly ChatSentimentLogRecord[]>
  ): Lazy<readonly ChatSentimentLogRecord[]> {
    return new Lazy(loadSentimentLog);
  }

  /**
   * Reconstruct a Conversation from its serialized form (e.g. from saga journal replay).
   * toolLoader and cocoToolKeys are optional; omit for read-only use (getRunKey, getLoadedTools, etc.).
   */
  static fromSerialized(
    serialized: SerializedConversation,
    options?: {
      toolLoader?: ToolLoader;
      workspaceDomainLoader?: WorkspaceDomainLoader;
      cocoToolKeys?: CocoToolKeys;
      referencedDocumentsById?: ReadonlyMap<string, DocumentState>;
    }
  ): Conversation {
    const messages: Message[] = serialized.messages.map((m) => ({
      uiMessage: m.uiMessage as UIMessage,
      type: m.type,
      readAt: m.readAt !== null ? new Date(m.readAt) : null,
      createdAt: m.createdAt !== undefined ? new Date(m.createdAt) : undefined,
      updatedAt: m.updatedAt !== undefined ? new Date(m.updatedAt) : undefined,
      status: m.status === 'streaming' ? 'streaming' : 'ready',
      ...(m.actorId !== undefined && { actorId: m.actorId }),
    }));
    const messagePositions = new Map<string, number>(
      Object.entries(serialized.messagePositions)
    );
    const messageReadAt = new Map<string, Date | null>();
    for (const [k, v] of Object.entries(serialized.messageReadAt)) {
      messageReadAt.set(k, v !== null ? new Date(v) : null);
    }
    const chat: ChatData | undefined = serialized.chat
      ? {
          id: serialized.chat.id,
          orgId: serialized.chat.orgId,
          actorId: serialized.chat.actorId,
          triggerSource: serialized.chat.triggerSource ?? null,
          agent: serialized.chat.agent,
          agentId: serialized.chat.agentId ?? null,
          title: serialized.chat.title,
          status: serialized.chat.status ?? 'active',
          parentId: serialized.chat.parentId ?? null,
          parentToolCallId: serialized.chat.parentToolCallId ?? null,
          folderId: serialized.chat.folderId ?? null,
          resourceId: serialized.chat.resourceId ?? null,
          ownerOrgId: serialized.chat.ownerOrgId ?? serialized.chat.orgId,
          metadata: serialized.chat.metadata,
        }
      : undefined;
    const actions: ChatActionData[] = serialized.actions.map((a) => ({
      id: a.id,
      actorId: a.actorId ?? serialized.chat?.actorId ?? null,
      chatId: a.chatId,
      type: a.type as ChatActionType,
      status: a.status as ChatActionStatus,
      data: a.data,
      completedAt: a.completedAt !== null ? new Date(a.completedAt) : null,
      approvedAt: a.approvedAt !== null ? new Date(a.approvedAt) : null,
      approvedBy: a.approvedBy,
      createdAt: new Date(a.createdAt),
      updatedAt: new Date(a.updatedAt),
    }));
    const loadedTools = serialized.loadedTools.map((s) =>
      ToolKey.fromString(s)
    );
    const childConversations =
      serialized.children !== undefined && serialized.children.length > 0
        ? serialized.children.map((c) =>
            Conversation.fromSerialized(c, options)
          )
        : [];
    const model: ChatModelData | undefined =
      serialized.model !== undefined
        ? {
            chatId: serialized.model.chatId,
            position: serialized.model.position,
            model: serialized.model.model,
          }
        : undefined;
    const unpersistedActionIds =
      serialized.unpersistedActionIds !== undefined
        ? new Set(serialized.unpersistedActionIds)
        : undefined;
    return Conversation.createFromParams({
      orgId: serialized.orgId,
      conversationId: serialized.conversationId,
      systemMessage: serialized.systemMessage ?? coco.SYSTEM_MESSAGE,
      messages,
      chat,
      loadedTools,
      messagePositions,
      actions,
      messageReadAt,
      ...(childConversations.length > 0 && { children: childConversations }),
      ...(model !== undefined && {
        model: Lazy.resolved(new ChatModel(model)),
      }),
      ...(unpersistedActionIds !== undefined && { unpersistedActionIds }),
      toolLoader: options?.toolLoader,
      workspaceDomainLoader: options?.workspaceDomainLoader,
      cocoToolKeys: options?.cocoToolKeys,
      ...(options?.referencedDocumentsById !== undefined && {
        referencedDocumentsById: options.referencedDocumentsById,
      }),
    });
  }

  static createRunKey(conversationId: string): string {
    return RunKeyHelpers.derive({
      t: 'workflow-program-repl',
      eraQLHash: conversationId,
    });
  }

  /** Default opts for agent input when writing to KVStore. */
  static readonly DEFAULT_KV_STORE_OPTS = {
    maxOutputTokens: null as number | null,
    topP: 0.9,
    topK: 50,
    temperature: 0.5,
    frequencyPenalty: 0.5,
    presencePenalty: 0.5,
  } as const;

  /** Params for building agent input to write to KVStore. */
  static buildKvStoreInputPayload(params: {
    chatId: string;
    /** Ephemeral suffix (onboarding, Slack, etc.) for skills document-reference scanning. */
    systemSuffix?: string;
    resumePosition?: number;
    toolKeys?: string[];
    excludedToolKeys?: string[];
    opts?: Partial<{
      maxOutputTokens: number | null;
      topP: number;
      topK: number;
      temperature: number;
      frequencyPenalty: number;
      presencePenalty: number;
    }>;
    /** @deprecated Legacy KV fields; ignored for new writes. */
    messages?: unknown[];
    agent?: string;
    agentId?: string | null;
    parentId?: string | null;
  }): Record<string, unknown> {
    const opts = {
      ...Conversation.DEFAULT_KV_STORE_OPTS,
      ...params.opts,
    };
    return {
      chatId: params.chatId,
      ...(params.systemSuffix !== undefined &&
        params.systemSuffix !== '' && { systemSuffix: params.systemSuffix }),
      ...(params.toolKeys !== undefined && { toolKeys: params.toolKeys }),
      ...(params.excludedToolKeys !== undefined &&
        params.excludedToolKeys.length > 0 && {
          excludedToolKeys: params.excludedToolKeys,
        }),
      ...(params.resumePosition !== undefined && {
        resumePosition: params.resumePosition,
      }),
      opts,
    };
  }

  /**
   * Writes agent input to KVStore. Builds the payload with sensible defaults
   * and delegates to the provided write function (e.g. from ConversationUtils
   * in the server).
   */
  static async writeKvStoreInput(
    params: {
      orgId: string;
      chatId: string;
      systemSuffix?: string;
      resumePosition?: number;
      toolKeys?: string[];
      excludedToolKeys?: string[];
      opts?: Partial<{
        maxOutputTokens: number | null;
        topP: number;
        topK: number;
        temperature: number;
        frequencyPenalty: number;
        presencePenalty: number;
      }>;
    },
    write: (orgId: string, payload: Record<string, unknown>) => Promise<string>
  ): Promise<string> {
    const payload = Conversation.buildKvStoreInputPayload(params);
    return write(params.orgId, payload);
  }

  title(): string {
    return this.chatModel?.title ?? '';
  }

  async model(): Promise<ChatModel | null> {
    if (this.conversationModel === undefined) {
      return ChatModel.fromSerialized({
        chatId: this.conversationId,
        position: 0, //TODO this might not be correct
        model: coco.DEFAULT_MODEL,
      });
    }
    return await this.conversationModel.get();
  }

  /**
   * Latest sentiment log rows when a loader was provided at construction; otherwise `[]`.
   */
  async sentimentLog(): Promise<readonly ChatSentimentLogRecord[]> {
    if (this.chatSentimentLog === undefined) {
      return [];
    }
    return await this.chatSentimentLog.get();
  }

  /**
   * Eval-ready snapshot of this conversation: facts, tool calls by turn,
   * sentiment, compactions, artifacts, result, and recursive sub-agent insights.
   */
  async insights(actorId: string): Promise<ConversationInsights> {
    const [facts, sentimentRecords, receipts, chatModel] = await Promise.all([
      this.facts(actorId),
      this.sentimentLog(),
      this.toolReceipts(),
      this.model(),
    ]);

    const resolveMessagePosition = (
      message: MessageClass,
      index: number
    ): number => this.stableMessagePosition(message, index);

    const messagesWithReceipts = applyReceiptsToMessages(
      this.getAllMessages(),
      receipts
    );
    const turns = buildTurnsFromMessages(
      messagesWithReceipts,
      resolveMessagePosition
    );
    const compactions = buildCompactionsFromMessages(
      this.getAllMessages(),
      resolveMessagePosition
    );

    const childConversations = this.children.filter(
      (child): child is Conversation => child instanceof Conversation
    );
    const subAgents = await Promise.all(
      childConversations.map((child) => child.insights(actorId))
    );

    return {
      conversationId: this.conversationId,
      parentToolCallId: this.parentToolCallId(),
      facts: mapFactsForInsights(facts),
      turns,
      result: serializeConversationResult(this.result(), (messageId) =>
        this.getPositionForMessageId(messageId)
      ),
      artifacts: this.ownChatArtifacts(),
      sentiment: foldSentimentLogRecords(sentimentRecords),
      compactions,
      counts: buildCountsFromTurns(this.getAllMessages(), turns),
      metadata: {
        triggerSource: this.triggeredBy(),
        agentId: this.getAgentId(),
        parentId: this.parent(),
        model: chatModel?.model ?? null,
      },
      llmUsageByMessageId: buildLlmUsageByMessageId(this.getAllMessages()),
      subAgents,
    };
  }

  /**
   * Get the parent chat ID (null if this is a root conversation).
   */
  parent(): string | null {
    return this.chatModel?.parentId ?? null;
  }

  /**
   * Get the parent's tool call ID that spawned this sub-agent (null if not a sub-agent or not set).
   */
  parentToolCallId(): string | null {
    return this.chatModel?.getParentToolCallId() ?? null;
  }

  /**
   * Get the agent ID (from agents table). Null if this is a user chat.
   */
  getAgentId(): string | null {
    return this.chatModel?.agentId ?? null;
  }

  /**
   * Legacy data-graph agent identity from `chats.agent`, when set.
   */
  getAgentIdentity(): string | null {
    return this.chatModel?.agentIdentity ?? null;
  }

  /** Tool keys this chat must not invoke unless overridden by @mentions (`chats.metadata.excludedToolKeys`). */
  getExcludedToolKeys(): readonly string[] {
    const fromMeta = this.chatModel?.getExcludedToolKeys() ?? [];
    const selfToolKey = this.linkedAgentSelfToolKey();
    if (selfToolKey === null || fromMeta.includes(selfToolKey)) {
      return fromMeta;
    }
    return [...fromMeta, selfToolKey];
  }

  /**
   * True when this chat runs *as* the linked saved agent (the agent's workspace
   * prompt is this chat's system prompt).
   *
   * False when the agent link only records a focused sub-agent: persisting a
   * `focus_agent` action upserts the `agent_chats` row, so agent-editor chats
   * carry an `agentId` even though they are Coco chats that merely *invoke*
   * that agent as a tool.
   */
  runsAsLinkedAgent(): boolean {
    const linkedAgentId = this.getLinkedAgent()?.id ?? this.getAgentId();
    if (linkedAgentId === null || linkedAgentId === '') {
      return false;
    }
    return this.getFocusedAgentId() !== linkedAgentId;
  }

  /**
   * Invocable `coco/agent-…` key for the saved agent this chat is linked to
   * (system prompt). Null when the chat is not an agent-linked run, including
   * when the agent is linked only as a focused tool ({@link runsAsLinkedAgent}).
   */
  protected linkedAgentSelfToolKey(): string | null {
    if (!this.runsAsLinkedAgent()) {
      return null;
    }
    const linkedAgent = this.getLinkedAgent();
    if (linkedAgent !== null) {
      return agentIdToAgentToolKey(linkedAgent.id);
    }
    const agentId = this.getAgentId();
    return agentId === null ? null : agentIdToAgentToolKey(agentId);
  }

  /**
   * Drop the linked agent's own tool key. The agent is already the system
   * prompt for this chat — exposing it as a tool enables self-delegation.
   */
  protected withoutLinkedAgentSelfToolKey(keys: readonly string[]): string[] {
    const selfToolKey = this.linkedAgentSelfToolKey();
    if (selfToolKey === null) {
      return [...keys];
    }
    return keys.filter((key) => key !== selfToolKey);
  }

  /**
   * Final tool keys for the LLM prompt and runtime execution guard.
   *
   * Candidates: {@link getLoadedTools} (history + load actions), system @mentions,
   * plus subclass-specific required tools.
   *
   * Rules vary by subclass. Only sub-agent conversations apply
   * {@link getExcludedToolKeys} beyond the linked-agent self-tool strip that
   * every subclass gets via {@link appendSkillBundledToolKeys}.
   */
  abstract getToolKeys(): Promise<readonly string[]>;

  /**
   * Saved agent hydrated from DB when the chat is linked via `agentId`.
   * Null when there is no link or the agent row was missing.
   */
  getLinkedAgent(): Agent | null {
    return this.linkedAgent ?? null;
  }

  getToolOrgId(toolKey: ToolKey): string | null {
    if (
      this.getIncludedTools().some((t) => t.toString() === toolKey.toString())
    ) {
      return this.linkedAgent?.orgId ?? this.chatModel?.ownerOrgId ?? null;
    }
    return this.chatModel?.ownerOrgId ?? null;
  }

  getToolUserId(toolKey: ToolKey): string | null {
    if (
      this.getIncludedTools().some((t) => t.toString() === toolKey.toString())
    ) {
      return this.linkedAgent?.ownerId ?? this.chatModel?.actorId ?? null;
    }
    return this.chatModel?.actorId ?? null;
  }

  // ============================================================================
  // Mutation Methods (modify this instance)
  // ============================================================================

  /**
   * Mark a tool as loaded by completing the ask/for_credentials tool call.
   * This is called after credentials are provided and the tool is successfully loaded.
   * Updates the ask/for_credentials tool call to output-available state and adds the tool to loadedTools.   */
  markToolAsLoaded(toolKey: ToolKey, _toolCallId: string): void {
    // Add tool to loaded tools if not already present
    if (!this.loadedTools.some((t) => t.toString() === toolKey.toString())) {
      this.loadedTools.push(toolKey);
    }
  }

  /**
   * Programmatically enqueue a tool call (string key + JSON params) on the last assistant
   * message, or create one — same shape as {@link askForInput} with `input-available` state.
   * Non-object params are stored as an empty object.
   */
  addToolCall(toolKey: string, params: unknown): void {
    const key = ToolKey.fromString(toolKey);
    const record: Record<string, unknown> =
      typeof params === 'object' && params !== null && !Array.isArray(params)
        ? (params as Record<string, unknown>)
        : {};
    this.askForInput({ toolKey: key, params: record });
  }

  /**
   * Adds a NEW assistant message carrying a single **parked** tool call, with a
   * caller-supplied toolCallId — a server-posted placeholder (e.g. an in-progress
   * file upload) that renders as its own entry and is completed later with
   * {@link completeToolCall} by the same toolCallId.
   *
   * The call is parked (not left input-available): it is written output-available
   * with the {@link incomplete} sigil, so it has `isIncomplete() === true` and
   * `needsExecution() === false`. That is what makes an ATC run stop here without
   * executing it — an input-available part with no output would instead be picked
   * up as an un-executed tool call and, since this placeholder is not a real
   * loaded tool, get an `execution-error` written for a tool the model never
   * called. Unlike {@link askForInput}, which appends to the last assistant
   * message, this always stands on its own.
   */
  addParkedToolCallMessage(params: {
    toolKey: ToolKey;
    input: Record<string, unknown>;
    toolCallId: string;
  }): void {
    this.pushStandaloneAssistantToolCall({
      type: `tool-${params.toolKey.toLlmName()}` as const,
      toolCallId: params.toolCallId,
      input: params.input,
      state: 'input-available' as const,
    });
    // Park it: convert to output-available carrying the incomplete sigil so the
    // agent loop stops here instead of trying to execute the placeholder.
    this.completeToolCall(params.toolCallId, incomplete({}));
    // Deliberately not added to loadedTools: this is a display-only placeholder
    // the server posts and completes itself, not a tool the model may call.
  }

  /**
   * Append a brand-new assistant message that stands on its own at the end of
   * the conversation, carrying a single input-available tool part. Tracks it as
   * new and assigns it the next position. Shared by
   * {@link addParkedToolCallMessage} and the empty-conversation branch of
   * {@link askForInput} so the two do not drift.
   */
  private pushStandaloneAssistantToolCall(toolPart: {
    type: `tool-${string}`;
    toolCallId: string;
    input: Record<string, unknown>;
    state: 'input-available';
  }): void {
    const newMessage: UIMessage = {
      id: generateMessageId(),
      role: 'assistant',
      parts: [toolPart],
    };
    const newMessageModel = MessageClass.fromUIMessage(
      this.conversationId,
      newMessage,
      'chat',
      this.actions
    );
    this.messages.push(newMessageModel);
    if (newMessageModel.id !== undefined) {
      this.newMessageIds.add(newMessageModel.id);
      const maxPosition =
        this.messagePositions.size > 0
          ? Math.max(...Array.from(this.messagePositions.values()))
          : -1;
      this.messagePositions.set(newMessageModel.id, maxPosition + 1);
    }
  }

  /**
   * Ask for input using a specific tool.
   * Adds a tool call with input-available state to the conversation.
   * If toolCallId is provided, replaces the existing tool call instead of creating a new one.   */
  askForInput(params: {
    toolKey: ToolKey;
    params: Record<string, unknown>;
    toolCallId?: string;
  }): void {
    if (params.toolCallId) {
      // Replace existing tool call
      this.replaceToolCall(params.toolCallId, {
        toolKey: params.toolKey,
        input: params.params,
        state: 'input-available',
      });
      return;
    }

    // Create new tool call (existing behavior)
    const toolCallId = generateToolCallId();
    const toolName = params.toolKey.toLlmName();

    // Find the last assistant message or create a new one
    let lastAssistantIndex = -1;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i]?.isAssistantMessage()) {
        lastAssistantIndex = i;
        break;
      }
    }

    const toolPart = {
      type: `tool-${toolName}` as const,
      toolCallId,
      input: params.params,
      state: 'input-available' as const,
    };

    if (lastAssistantIndex >= 0) {
      // Add to existing assistant message
      const msgModel = this.messages[lastAssistantIndex]!;
      msgModel.addPart(toolPart);
      this.markMessageAsModified(msgModel.id);
    } else {
      // No assistant message to append to — stand the tool call up on its own.
      this.pushStandaloneAssistantToolCall(toolPart);
    }
  }

  /**
   * Replace a pending tool invocation with the ask-for-credentials tool, preserving
   * `toolCallId` so the UI can collect credentials. `next` records the original call so the
   * ask-for-credentials tool can re-queue it after configuration (same idea as {@link askForInput}
   * with `toolCallId`).
   */
  askForCredentialsReplacingToolCall(params: {
    toolCallId: string;
    deferredToolKey: ToolKey;
    deferredParams: Record<string, unknown>;
  }): void {
    const credentialToolKey = params.deferredToolKey.toString();
    this.replaceToolCall(params.toolCallId, {
      toolKey: ToolKey.fromString(COCO_ASK_FOR_CREDENTIALS_TOOL_KEY),
      input: {
        toolKey: credentialToolKey,
        next: {
          toolKey: credentialToolKey,
          params: params.deferredParams,
        },
      },
      state: 'input-available',
    });
  }

  /**
   * Add a completed tool call (both the tool call and its result) to the conversation.
   * Creates a tool call part with output-available state and immediately adds the result part.   */
  addCompletedToolCall(params: {
    toolKey: ToolKey;
    input: Record<string, unknown>;
    output: unknown;
    toolCallId?: string;
    providerMetadata?: Record<string, unknown>;
  }): void {
    const toolCallId = params.toolCallId ?? generateToolCallId();
    const toolName = params.toolKey.toLlmName();

    // Find the last assistant message or create a new one
    let lastAssistantIndex = -1;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i]?.isAssistantMessage()) {
        lastAssistantIndex = i;
        break;
      }
    }

    const completedPart = this.buildCompletedToolCallPart(
      toolName,
      toolCallId,
      params.input,
      params.output,
      params.providerMetadata
    );

    if (lastAssistantIndex >= 0) {
      // Add to existing assistant message
      const msgModel = this.messages[lastAssistantIndex]!;
      msgModel.addPart(completedPart);
      this.markMessageAsModified(msgModel.id);
    } else {
      // Create new assistant message
      const newMessage: UIMessage = {
        id: generateMessageId(),
        role: 'assistant',
        parts: [completedPart],
      };
      const newMessageModel = MessageClass.fromUIMessage(
        this.conversationId,
        newMessage,
        'chat',
        this.actions
      );
      this.messages.push(newMessageModel);
      if (newMessageModel.id !== undefined) {
        this.newMessageIds.add(newMessageModel.id);
        // Assign position for new message
        const maxPosition =
          this.messagePositions.size > 0
            ? Math.max(...Array.from(this.messagePositions.values()))
            : -1;
        const nextPosition = maxPosition + 1;
        this.messagePositions.set(newMessageModel.id, nextPosition);
      }
    }

    // Add tool to loaded tools if not already present
    if (
      !this.loadedTools.some((t) => t.toString() === params.toolKey.toString())
    ) {
      this.loadedTools.push(params.toolKey);
    }
  }

  /** The single output-available part for one completed tool invocation. */
  private buildCompletedToolCallPart(
    toolName: string,
    toolCallId: string,
    input: Record<string, unknown>,
    output: unknown,
    providerMetadata?: Record<string, unknown>
  ): UIMessage['parts'][number] {
    // A completed invocation is a SINGLE output-available part. The AI SDK
    // expects one UI part per toolCallId; when convertToModelMessages runs, that
    // one part (with output) produces both a tool-call and a tool-result in the
    // model prompt (see completeToolCall). Emitting a separate outputless "call"
    // part alongside it produced a part that fails wire validation (output-available
    // requires output) AND a malformed prompt with duplicate tool-call/tool-result
    // ids for the same invocation — which providers reject.
    return {
      type: `tool-${toolName}` as const,
      toolCallId,
      input,
      output,
      state: 'output-available' as const,
      ...(providerMetadata ? { providerMetadata } : {}),
    } as any;
  }

  /**
   * Append a completed tool call (call + result parts) to a specific message,
   * so that message also renders the tool's result. Used to persist a
   * client-triggered artifact — e.g. materializing a markdown table into a
   * dataset — onto the exact message it came from. Falls back to the normal
   * last-assistant behavior when the target message can't be found.
   */
  addCompletedToolCallToMessage(params: {
    messageId: string;
    toolKey: ToolKey;
    input: Record<string, unknown>;
    output: unknown;
    toolCallId?: string;
  }): void {
    const msgModel = this.messages.find(
      (message) => message.id === params.messageId
    );
    if (
      msgModel === undefined ||
      msgModel.id === undefined ||
      !msgModel.isAssistantMessage()
    ) {
      this.addCompletedToolCall({
        toolKey: params.toolKey,
        input: params.input,
        output: params.output,
        toolCallId: params.toolCallId,
      });
      return;
    }

    const toolCallId = params.toolCallId ?? generateToolCallId();
    const completedPart = this.buildCompletedToolCallPart(
      params.toolKey.toLlmName(),
      toolCallId,
      params.input,
      params.output
    );
    msgModel.addPart(completedPart);
    this.markMessageAsModified(msgModel.id);

    if (
      !this.loadedTools.some((t) => t.toString() === params.toolKey.toString())
    ) {
      this.loadedTools.push(params.toolKey);
    }
  }

  /**
   * Complete a tool call by adding its result.
   * Keeps the tool-call part and updates it in place with the result (Vercel AI SDK style:
   * one part per invocation that gets updated to output-available). Idempotent: if a result
   * part already exists, it will be updated instead of creating a duplicate.
   *
   * `opts.ingot` is the receipt from storing this result in the chat's ingot;
   * it is kept at `callProviderMetadata.cotera.ingot` for the renderer.
   */
  completeToolCall(
    toolCallId: string,
    result: unknown,
    opts?: { ingot?: IngotToolReceipt }
  ): void {
    // Find the message containing this tool call
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      // Find the tool call part and result part (hydrated)
      const toolCallPart = msgModel.findToolCallPartWithoutOutput(toolCallId);
      const resultToolPart = msgModel.findResultToolPart(toolCallId);

      if (!toolCallPart && !resultToolPart) {
        // No tool call found in this message, continue to next message
        continue;
      }

      // Get tool name from tool call part or result part
      const toolName =
        toolCallPart?.toolKey().toLlmName() ??
        resultToolPart?.toolKey().toLlmName() ??
        'unknown';

      // Get input from the call part if it existed, otherwise from the result part
      const input =
        ((toolCallPart?.input ?? resultToolPart?.input) as Record<
          string,
          unknown
        >) ?? {};

      // Determine state based on result type:
      // - execution-error: tool ran and failed (e.g. API error) → output-error, LLM sees error
      // - error: denial/cancellation → output-denied with approval
      // - otherwise → output-available
      const isExecutionError =
        typeof result === 'object' &&
        result !== null &&
        'type' in result &&
        (result as { type?: string }).type === 'execution-error';
      const isDenialError =
        typeof result === 'object' &&
        result !== null &&
        'type' in result &&
        (result as { type?: string }).type === 'error';

      const isToolFailureEnvelope = isToolFailureErrorEnvelopeResult(result);

      const errorMessage =
        typeof result === 'object' &&
        result !== null &&
        'message' in result &&
        typeof (result as { message?: unknown }).message === 'string'
          ? (result as { message: string }).message
          : isToolFailureEnvelope
          ? (result as { error: string }).error
          : undefined;

      const state: 'output-available' | 'output-denied' | 'output-error' =
        isExecutionError || isToolFailureEnvelope
          ? 'output-error'
          : isDenialError
          ? 'output-denied'
          : 'output-available';

      // Update in place: replace the existing tool-call (or result) part with the completed
      // part that includes the result. Do not remove the tool-call message. The AI SDK
      // expects one UI part per invocation; when convertToModelMessages runs, that one
      // part (with output) produces both a tool-call and a tool-result in the model
      // prompt.
      // Preserve callProviderMetadata (e.g. thoughtSignature for Gemini 3) - required for
      // multi-step function calling. Without it, Gemini 3 returns "Function call is
      // missing a thought_signature" when sending the next request.
      const sourcePart = toolCallPart ?? resultToolPart;
      const rawPart = sourcePart?.raw() as
        | {
            callProviderMetadata?: Record<string, unknown>;
            providerMetadata?: Record<string, unknown>;
          }
        | undefined;
      const sourceMetadata: Record<string, unknown> | undefined =
        rawPart?.callProviderMetadata ?? rawPart?.providerMetadata;
      const callProviderMetadata: Record<string, unknown> | undefined =
        opts?.ingot === undefined
          ? sourceMetadata
          : withIngotReceipt(sourceMetadata, opts.ingot);

      // AI SDK convertToModelMessages expects approval.id and approval.reason for output-denied
      // parts. Without approval.id, tool-approval-response has approvalId: undefined, causing
      // "The messages do not match the ModelMessage[] schema" when standardizePrompt validates.
      // For output-error the SDK type has output?: never; only errorText is used for the model.
      const completedPart = {
        type: `tool-${toolName}` as const,
        toolCallId,
        input,
        // Coerce an undefined result to null so the `output` key survives JSON
        // serialization. output-available/output-denied parts REQUIRE an output;
        // an undefined value drops the key and produces a part that fails wire
        // validation, permanently wedging the chat on the next send.
        ...(state !== 'output-error' ? { output: result ?? null } : {}),
        state,
        ...(state === 'output-error' && errorMessage !== undefined
          ? { errorText: errorMessage }
          : {}),
        ...(state === 'output-denied' && errorMessage !== undefined
          ? {
              approval: {
                id: generateApprovalId(),
                approved: false as const,
                reason: errorMessage,
              },
            }
          : {}),
        ...(callProviderMetadata !== undefined ? { callProviderMetadata } : {}),
      } as any;

      const replaced = msgModel.replaceToolCallPart(toolCallId, completedPart);
      if (replaced) {
        this.markMessageAsModified(msgModel.id);
        return;
      }
    }

    throw new Error(
      `Tool call with ID ${toolCallId} not found in conversation messages`
    );
  }

  /**
   * Mark a tool call as awaiting input (paused for user approval).
   * Similar to completeToolCall but sets state to 'input-available' instead of 'output-available'.
   * Used when a tool execution is paused in manual mode.
   * No result is provided since the tool hasn't executed yet.   */
  markToolAsAwaitingInput(toolCallId: string): void {
    // Find the message containing this tool call
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      // Find the tool call part and result part (hydrated)
      const toolCallPart = msgModel.findToolCallPartWithoutOutput(toolCallId);
      const resultToolPart = msgModel.findResultToolPart(toolCallId);

      if (!toolCallPart && !resultToolPart) {
        // No tool call found in this message, continue to next message
        continue;
      }

      // Get tool name from tool call part or result part
      const toolName =
        toolCallPart?.toolKey().toLlmName() ??
        resultToolPart?.toolKey().toLlmName() ??
        'unknown';

      // Remove all existing tool call parts for this toolCallId
      msgModel.removeToolCallParts(toolCallId);

      // Get input and callProviderMetadata from the call part or result part
      const sourcePart = toolCallPart ?? resultToolPart;
      const input = (sourcePart?.input as Record<string, unknown>) ?? {};
      const rawPart = sourcePart?.raw() as
        | {
            callProviderMetadata?: Record<string, unknown>;
            providerMetadata?: Record<string, unknown>;
          }
        | undefined;
      const callProviderMetadata: Record<string, unknown> | undefined =
        rawPart?.callProviderMetadata ?? rawPart?.providerMetadata;

      // Add single result part
      const resultPart = {
        type: `tool-${toolName}` as const,
        toolCallId,
        input,
        output: undefined,
        state: 'input-available' as const,
        ...(callProviderMetadata ? { callProviderMetadata } : {}),
      } as any;

      msgModel.addPart(resultPart);
      this.markMessageAsModified(msgModel.id);
      return;
    }

    throw new Error(
      `Tool call with ID ${toolCallId} not found in conversation messages`
    );
  }

  /**
   * Replace a tool call with a new tool call part.
   * Finds the tool call by toolCallId and replaces it entirely.
   * If the tool call doesn't exist yet (e.g., query tool not persisted), adds it instead.
   * Useful for updating tool call input, state, or replacing with a different tool.   */
  replaceToolCall(
    toolCallId: string,
    replacement: {
      toolKey: ToolKey;
      input: Record<string, unknown>;
      state?: 'input-available' | 'output-available' | 'output-denied';
      output?: unknown;
      providerMetadata?: Record<string, unknown>;
    }
  ): void {
    // Find the message containing this tool call
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      const toolCallPart = msgModel.findToolCallPart(toolCallId);
      if (toolCallPart) {
        // Found the tool call, replace it
        const toolName = replacement.toolKey.toLlmName();
        const newToolPart = {
          type: `tool-${toolName}` as const,
          toolCallId,
          input: replacement.input,
          state: replacement.state ?? 'input-available',
          ...(replacement.output !== undefined
            ? { output: replacement.output }
            : {}),
          ...(replacement.providerMetadata
            ? { providerMetadata: replacement.providerMetadata }
            : {}),
        } as any;

        msgModel.replaceToolCallPart(toolCallId, newToolPart);
        this.markMessageAsModified(msgModel.id);
        return;
      }
    }

    // Tool call not found - this can happen for query tools that execute before being persisted
    // Add it to the last assistant message or create a new one
    const toolName = replacement.toolKey.toLlmName();
    const newToolPart = {
      type: `tool-${toolName}` as const,
      toolCallId,
      input: replacement.input,
      state: replacement.state ?? 'input-available',
      ...(replacement.output !== undefined
        ? { output: replacement.output }
        : {}),
      ...(replacement.providerMetadata
        ? { providerMetadata: replacement.providerMetadata }
        : {}),
    } as any;

    // Find the last assistant message
    let lastAssistantIndex = -1;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i]?.isAssistantMessage()) {
        lastAssistantIndex = i;
        break;
      }
    }

    if (lastAssistantIndex >= 0) {
      // Add to existing assistant message
      const msgModel = this.messages[lastAssistantIndex]!;
      msgModel.addPart(newToolPart);
      this.markMessageAsModified(msgModel.id);
    } else {
      // Create new assistant message
      const newMessage: UIMessage = {
        id: generateMessageId(),
        role: 'assistant',
        parts: [newToolPart],
      };
      const newMessageModel = MessageClass.fromUIMessage(
        this.conversationId,
        newMessage,
        'chat',
        this.actions
      );
      this.messages.push(newMessageModel);
      if (newMessageModel.id !== undefined) {
        this.newMessageIds.add(newMessageModel.id);
        const maxPosition =
          this.messagePositions.size > 0
            ? Math.max(...Array.from(this.messagePositions.values()))
            : -1;
        const nextPosition = maxPosition + 1;
        this.messagePositions.set(newMessageModel.id, nextPosition);
      }
    }
  }

  /**
   * Add providerMetadata to a tool call.
   * Merges with existing providerMetadata if present.
   * Ensures the tool call exists in the messages array.   */
  addProviderMetadataToToolCall(
    toolCallId: string,
    providerMetadata: Record<string, unknown>
  ): void {
    // Find the message containing this tool call
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      const toolCallPart = msgModel.findToolCallPart(toolCallId);
      if (toolCallPart) {
        // Merge with existing providerMetadata if present
        const existingMetadata =
          'providerMetadata' in toolCallPart &&
          typeof toolCallPart.providerMetadata === 'object' &&
          toolCallPart.providerMetadata !== null
            ? (toolCallPart.providerMetadata as Record<string, unknown>)
            : {};

        const mergedMetadata = {
          ...existingMetadata,
          ...providerMetadata,
        };

        // Create updated tool call part with merged metadata
        const updatedToolPart = {
          ...toolCallPart,
          providerMetadata: mergedMetadata,
        } as any;

        msgModel.replaceToolCallPart(toolCallId, updatedToolPart);
        this.markMessageAsModified(msgModel.id);
        return;
      }
    }

    throw new Error(
      `Tool call with ID ${toolCallId} not found in conversation messages`
    );
  }

  /**
   * Update the output of a tool call part (e.g. for streaming).
   * Merges output with existing output. Use preliminary: true for in-progress streaming.
   */
  updateToolPartOutput(
    toolCallId: string,
    output: unknown,
    options?: { preliminary?: boolean }
  ): void {
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      const toolCallPart = msgModel.findToolCallPart(toolCallId);
      if (
        toolCallPart &&
        typeof toolCallPart.type === 'string' &&
        toolCallPart.type.startsWith('tool-') &&
        'toolCallId' in toolCallPart
      ) {
        const existingOutput =
          'output' in toolCallPart && toolCallPart.output !== undefined
            ? (toolCallPart.output as Record<string, unknown>)
            : {};
        const mergedOutput =
          typeof output === 'object' && output !== null
            ? { ...existingOutput, ...(output as Record<string, unknown>) }
            : output;

        const preliminary =
          options?.preliminary !== undefined ? options.preliminary : true;
        const updatedToolPart = {
          ...toolCallPart,
          type: toolCallPart.type,
          toolCallId: toolCallPart.toolCallId,
          output: mergedOutput,
          state: 'output-available' as const,
          preliminary,
        };

        msgModel.replaceToolCallPart(toolCallId, updatedToolPart as ToolUIPart);
        this.markMessageAsModified(msgModel.id);
        return;
      }
    }

    throw new Error(
      `Tool call with ID ${toolCallId} not found in conversation messages`
    );
  }

  /**
   * Update the input of a tool call part with incomplete tool response data.
   * Merges the initial input with { '@@cotera-tool-incomplete': toolResponse } so the
   * frontend can read partial data (e.g. subAgentChatId) while the tool is still running.
   */
  updateToolInput(toolCallId: string, toolResponse: unknown): void {
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel || !msgModel.isAssistantMessage()) {
        continue;
      }

      const toolCallPart = msgModel.findToolCallPart(toolCallId);
      if (
        toolCallPart &&
        typeof toolCallPart.type === 'string' &&
        toolCallPart.type.startsWith('tool-') &&
        'toolCallId' in toolCallPart
      ) {
        const existingInput =
          typeof toolCallPart.input === 'object' &&
          toolCallPart.input !== null &&
          !Array.isArray(toolCallPart.input)
            ? (toolCallPart.input as Record<string, unknown>)
            : {};
        const mergedInput = {
          ...existingInput,
          '@@cotera-tool-incomplete': toolResponse,
        };

        const updatedToolPart = {
          ...toolCallPart,
          type: toolCallPart.type,
          toolCallId: toolCallPart.toolCallId,
          input: mergedInput,
          state: toolCallPart.state ?? 'input-available',
        };

        msgModel.replaceToolCallPart(toolCallId, updatedToolPart as ToolUIPart);
        this.markMessageAsModified(msgModel.id);
        return;
      }
    }

    throw new Error(
      `Tool call with ID ${toolCallId} not found in conversation messages`
    );
  }

  interruptToolCall(toolCallId: string, input: Record<string, unknown>): void {
    const action = InterruptedToolInputAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      toolCallId,
      toolInput: input,
    });

    this.actions.push(action);
    this.newActionIds.add(action.id);

    this.markToolAsAwaitingInput(toolCallId);
  }

  provideInterruptedToolData(
    toolCallId: string,
    data: Record<string, unknown>
  ): void {
    const action = InterruptedToolDataProvidedAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      toolCallId,
      toolInput: data,
    });

    this.actions.push(action);
    this.newActionIds.add(action.id);
  }

  /**
   * Cancel all incomplete tool calls by marking them as complete with a cancellation error.
   * Used when stopping a chat to ensure no tool calls remain in a pending state.
   * @param options.deniedBy - User ID for denying pending tool approvals. Defaults to SYSTEM_USER_USER_ID when omitted.
   */
  cancelIncompleteToolCalls(options?: { deniedBy?: string }): void {
    const deniedBy = options?.deniedBy ?? SYSTEM_USER_USER_ID;

    const incompleteToolCallIds = new Set<string>();
    for (const msgModel of this.messages) {
      if (!msgModel?.isAssistantMessage()) {
        continue;
      }
      const toolParts = msgModel.getToolParts();
      for (const toolPart of toolParts) {
        if (toolPart.isIncomplete()) {
          incompleteToolCallIds.add(toolPart.toolCallId);
        }
      }
    }

    for (const toolCallId of incompleteToolCallIds) {
      this.finalizeInterruptedToolCall(
        toolCallId,
        deniedBy,
        'Tool call cancelled'
      );
    }
  }

  private finalizeInterruptedToolCall(
    toolCallId: string,
    deniedBy: string,
    cancellationReason: string
  ): void {
    const cancellationError = {
      type: 'error' as const,
      message: cancellationReason,
    };

    const pendingAction = this.actions.find(
      (a) =>
        a.isToolApprovalAction() &&
        a.isPending() &&
        a.getToolCallId() === toolCallId
    );
    if (pendingAction !== undefined) {
      this.denyToolAction(pendingAction.id, deniedBy, cancellationReason);
    } else {
      this.completeToolCall(toolCallId, cancellationError);
    }
  }

  private stableMessagePosition(
    message: MessageClass,
    fallbackIndex: number
  ): number {
    if (message.id === undefined) {
      return fallbackIndex;
    }
    const position = this.messagePositions.get(message.id);
    return position ?? fallbackIndex;
  }

  /**
   * Visible assistant row is terminal: not streaming, has at least one part, and no incomplete
   * tool parts (same per-row notion as in {@link shouldYieldControlBackToTheLlm}).
   */
  private isFinalizedVisibleAssistantMessage(message: MessageClass): boolean {
    if (!message.isAssistantMessage() || message.isHidden()) {
      return false;
    }
    if (message.status === 'streaming') {
      return false;
    }
    const parts = message.raw().parts ?? [];
    if (parts.length === 0) {
      return false;
    }
    if (message.id !== undefined) {
      return !this.hasIncompleteToolCallsInMessage(message.id);
    }
    return !message.getToolParts().some((p) => p.isIncomplete());
  }

  private hasVisibleUserMessageAfterPosition(thanPosition: number): boolean {
    for (const [index, message] of this.messages.entries()) {
      if (!message.isUserMessage() || message.isHidden()) {
        continue;
      }
      const position = this.stableMessagePosition(message, index);
      if (position > thanPosition) {
        return true;
      }
    }
    return false;
  }

  /**
   * Cancel stale assistant work when a newer user message superseded it (e.g. user followed up
   * while tools were pending or a streaming shell never closed). Runs at hydrate time so the next
   * {@link Conversation.save}-equivalent persistence picks up terminal rows.
   */
  private selfHealInterruptedAssistantFlows(): void {
    const deniedBy = SYSTEM_USER_USER_ID;

    for (const [index, msgModel] of this.messages.entries()) {
      if (
        !msgModel.isAssistantMessage() ||
        msgModel.isHidden() ||
        msgModel.isCompactionMessage()
      ) {
        continue;
      }

      const assistantPosition = this.stableMessagePosition(msgModel, index);
      if (!this.hasVisibleUserMessageAfterPosition(assistantPosition)) {
        continue;
      }

      let needsConversationModifyMarker = false;

      if (msgModel.status === 'streaming') {
        msgModel.markInterruptedAssistantTerminal();
        needsConversationModifyMarker = true;
      }

      const toolParts = msgModel.getToolParts();
      for (const toolPart of toolParts) {
        if (toolPart.isIncomplete()) {
          this.finalizeInterruptedToolCall(
            toolPart.toolCallId,
            deniedBy,
            'Tool call cancelled'
          );
        }
      }

      const rawPartsAfterTools = msgModel.raw().parts ?? [];
      if (rawPartsAfterTools.length === 0) {
        msgModel.addPart({ type: 'text', text: '' });
        needsConversationModifyMarker = true;
      }

      if (needsConversationModifyMarker && msgModel.id !== undefined) {
        this.markMessageAsModified(msgModel.id);
      }
    }
  }

  /**
   * Load a tool and check if it can be executed.
   * If not executable, automatically calls askForCredentials.   */
  async loadTool(toolKey: ToolKey): Promise<void> {
    if (!this.toolLoader) {
      throw new Error('ToolLoader is required for loadOrAskForTool');
    }

    const userId = this.getToolUserId(toolKey);
    const tools = await this.toolLoader.loadTools([toolKey.toString()], {
      orgId: this.orgId,
      userId: userId === null ? undefined : userId,
    });
    const tool = tools[0] ?? null;

    if (!tool) {
      throw new Error(`Tool ${toolKey.toString()} not found`);
    }

    if (!tool.canExecute()) {
      // Tool cannot be executed
      return;
    }

    // Tool can be executed, add to loaded tools
    if (!this.loadedTools.some((t) => t.toString() === toolKey.toString())) {
      this.loadedTools.push(toolKey);
    }
  }

  /**
   * Record a loaded resource (aligned with `resource/get` types: chat, agent, folder, trigger).
   * For `agent`, the conversation also exposes the derived `coco/agent-*` tool via {@link getLoadedTools}.
   * Returns the new action id.
   */
  loadResource(params: {
    resourceType: ChatLoadedResourceType;
    resourceId: string;
    snapshot?: unknown;
  }): string {
    const action = LoadResourceAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      resourceType: params.resourceType,
      resourceId: params.resourceId.trim(),
      snapshot: params.snapshot,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Remove a resource from the conversation context (mirrors unload for tools).
   * When `resourceType` is `agent`, the derived agent tool is dropped from loaded tools the same way as `remove_tool`.
   * @param immediate - When true (default), creates a completed action and drops an in-memory agent tool key if present.
   */
  unloadResource(params: {
    resourceType: ChatLoadedResourceType;
    resourceId: string;
    immediate?: boolean;
  }): string {
    const immediate = params.immediate !== false;
    const resourceId = params.resourceId.trim();
    const action = UnloadResourceAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      resourceType: params.resourceType,
      resourceId,
      status: immediate ? 'completed' : 'pending',
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);

    if (immediate && params.resourceType === 'agent') {
      const toolKeyStr = agentIdToAgentToolKey(resourceId);
      this.loadedTools = this.loadedTools.filter(
        (t) => t.toString() !== toolKeyStr
      );
    }
    return action.id;
  }

  /**
   * Active resources from completed `load_resource` / `unload_resource` actions (timeline order).
   */
  getLoadedResources(): LoadedResourceDescriptor[] {
    // Tie-break on push order: load + unload in the same ms used to sort by UUID
    // and could apply unload before load, leaving the resource still "active".
    const indexed = this.actions.map((action, index) => ({ action, index }));
    indexed.sort((a, b) => {
      const timeCmp =
        a.action.actionData.createdAt.getTime() -
        b.action.actionData.createdAt.getTime();
      if (timeCmp !== 0) {
        return timeCmp;
      }
      return a.index - b.index;
    });
    const ordered = indexed.map(({ action }) => action);
    const active = new Map<string, LoadedResourceDescriptor>();
    const keyOf = (t: ChatLoadedResourceType, id: string) => `${t}:${id}`;

    for (const action of ordered) {
      if (action.isLoadResourceAction() && action.isCompleted()) {
        const load = action as LoadResourceAction;
        const d = load.tryGetDescriptor();
        if (d === null) {
          continue;
        }
        active.set(keyOf(d.resourceType, d.resourceId), {
          resourceType: d.resourceType,
          resourceId: d.resourceId,
          snapshot: d.snapshot,
        });
      } else if (action.isUnloadResourceAction() && action.isCompleted()) {
        const unload = action as UnloadResourceAction;
        try {
          active.delete(
            keyOf(unload.getResourceType(), unload.getResourceId())
          );
        } catch {
          // ignore malformed unload
        }
      }
    }
    return Array.from(active.values());
  }

  /**
   * Record a loaded developer skill. Returns the new action id.
   */
  loadSkill(params: { skillKey: string }): string {
    const action = LoadSkillAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      skillKey: params.skillKey.trim(),
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Active skill keys from completed `load_skill` actions (timeline order).
   */
  getLoadedSkills(): string[] {
    const indexed = this.actions.map((action, index) => ({ action, index }));
    indexed.sort((a, b) => {
      const timeCmp =
        a.action.actionData.createdAt.getTime() -
        b.action.actionData.createdAt.getTime();
      if (timeCmp !== 0) {
        return timeCmp;
      }
      return a.index - b.index;
    });
    const ordered = indexed.map(({ action }) => action);
    const active = new Set<string>();

    for (const action of ordered) {
      if (action.isLoadSkillAction() && action.isCompleted()) {
        try {
          active.add((action as LoadSkillAction).getSkillKey());
        } catch {
          // ignore malformed load_skill
        }
      }
    }
    return Array.from(active);
  }

  /**
   * Skill keys active for prompt resolution: persisted loads plus autoload skills
   * when a {@link SkillLoader} is attached.
   */
  async getActiveSkillKeys(): Promise<readonly string[]> {
    if (this.skillLoader !== undefined) {
      return this.skillLoader.getActiveSkillKeys(this);
    }
    return this.getLoadedSkills();
  }

  /**
   * Bootstrap skills available from chat creation (e.g. ATC {@link withSkills}).
   * Persists completed `load_skill` actions so reload matches prompt resolution.
   */
  seedPersistedLoadedSkills(skillKeys: readonly string[]): void {
    for (const skillKey of skillKeys) {
      const trimmed = skillKey.trim();
      if (trimmed === '') {
        continue;
      }
      const alreadyPersisted = this.actions.some((action) => {
        if (!action.isLoadSkillAction()) {
          return false;
        }
        if (!action.isCompleted()) {
          return false;
        }
        try {
          return (action as LoadSkillAction).getSkillKey() === trimmed;
        } catch {
          return false;
        }
      });
      if (alreadyPersisted) {
        continue;
      }
      this.loadSkill({ skillKey: trimmed });
    }
  }

  /**
   * Add an artifact to the conversation programmatically.
   * Creates a load_artifact action that is included in {@link Conversation.artifacts}.
   * Returns the action ID.
   */
  addArtifact(params: AddArtifactParams): string {
    const artifactId =
      params.artifactId ?? `artifact-${generateMessageId().slice(-8)}`;
    const action = LoadArtifactAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      artifactType: params.artifactType,
      artifactId,
      artifactData: params.data ?? null,
      toolCallId: params.toolCallId,
    });

    this.actions.push(action);
    this.newActionIds.add(action.id);

    return action.id;
  }

  /**
   * Dismiss an artifact from the conversation — the inverse of
   * {@link Conversation.addArtifact}. Creates a `remove_artifact` action that
   * suppresses this `artifactId` in {@link Conversation.artifacts} regardless of
   * where it came from (a `load_artifact` action, a tool output, a parent chat).
   *
   * Not a delete: the originating action or tool output stays in the transcript,
   * and anything that adds the same artifact afterwards brings it back.
   * Returns the action ID.
   */
  removeArtifact(artifactId: string): string {
    const action = RemoveArtifactAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      artifactId,
    });

    this.actions.push(action);
    this.newActionIds.add(action.id);

    return action.id;
  }

  hasFocusedAgent(): boolean {
    return this.getFocusedAgentId() !== null;
  }

  /**
   * Focus on a specific agent. The LLM will be instructed to prioritize using
   * this subagent for any task. Only one agent can be focused at a time;
   * focusing a new agent replaces the previous focus.
   * Returns the action ID.
   */
  focusAgent(agentId: string): string {
    const action = FocusAgentAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      agentId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);

    // Add agent tool to loaded tools so it's available for the LLM
    const agentToolKey = ToolKey.fromString(agentIdToAgentToolKey(agentId));
    if (
      !this.loadedTools.some((t) => t.toString() === agentToolKey.toString())
    ) {
      this.loadedTools.push(agentToolKey);
    }

    return action.id;
  }

  /**
   * Clear the focused agent. The LLM will no longer prioritize any specific subagent.
   * Returns the action ID.
   */
  unfocusAgent(): string {
    const action = UnfocusAgentAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Record the expected structured output shape. Values are persisted as EraQL text;
   * subsequent calls append newer actions; {@link Conversation.outputShape} returns the
   * latest completed `output_shape` action (defaulting to `super` when none or invalid).
   * Returns the action ID.
   */
  setOutputShape(shape: Ty.ExtendedAttributeType | string): string {
    const shapeStr = typeof shape === 'string' ? shape : Ty.displayTy(shape);
    const action = OutputShapeAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      shape: shapeStr,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Expected structured output type from the latest completed `output_shape` action.
   * Uses `createdAt` with array index as tiebreaker when timestamps match.
   * Defaults to the primitive `super` type when there is no action, malformed payload, or parse error.
   */
  outputShape(): Ty.ExtendedAttributeType {
    const shapeActions = this.actions
      .map((a, index) => ({ action: a, index }))
      .filter(
        ({ action }) => action.isOutputShapeAction() && action.isCompleted()
      )
      .sort(
        (a, b) =>
          b.action.actionData.createdAt.getTime() -
            a.action.actionData.createdAt.getTime() || b.index - a.index
      );

    const mostRecent = shapeActions[0];
    if (mostRecent === undefined) {
      return Ty.ty('super');
    }
    const action = mostRecent.action;
    if (!action.isOutputShapeAction()) {
      return Ty.ty('super');
    }
    let raw: string;
    try {
      raw = action.getShape();
    } catch {
      return Ty.ty('super');
    }
    const parsed = EraQL.parseTy(raw);
    if (parsed.isErr()) {
      return Ty.ty('super');
    }
    return parsed.value;
  }

  /**
   * Enable auto-approve mode. Tool calls will be automatically approved without manual intervention.
   * Returns the action ID.
   */
  autoApprove(): string {
    const action = AutoApproveAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Disable auto-approve mode. Reverts to manual approval where tool calls require user approval.
   * Returns the action ID.
   */
  removeAutoApprove(): string {
    const action = RemoveAutoApproveAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Get the approval mode: 'auto_approve' or 'manual'.
   * Derived from the most recent auto_approve or remove_auto_approve action.
   * Defaults to 'manual' when no such actions exist.
   */
  async mode(): Promise<'auto_approve' | 'manual'> {
    if (this.parentConversation !== undefined) {
      const parent = await this.parentConversation.get();
      if (parent === null) {
        return 'manual';
      }
      return await parent.mode();
    }
    const modeActions = this.actions
      .map((a, index) => ({ action: a, index }))
      .filter(
        ({ action }) =>
          action.type === 'auto_approve' ||
          action.type === 'remove_auto_approve'
      )
      .sort(
        (a, b) =>
          b.action.actionData.createdAt.getTime() -
            a.action.actionData.createdAt.getTime() || b.index - a.index
      );

    const mostRecent = modeActions[0];
    if (mostRecent === undefined) {
      return 'manual';
    }
    return mostRecent.action.type === 'auto_approve'
      ? 'auto_approve'
      : 'manual';
  }

  /**
   * Enable plan mode. Loads architecture skills + plan-first behavior for this conversation.
   * Returns the action ID.
   */
  enterPlanMode(): string {
    const action = EnterPlanModeAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Disable plan mode. Returns the action ID.
   */
  exitPlanMode(): string {
    const action = ExitPlanModeAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
    });
    this.actions.push(action);
    this.newActionIds.add(action.id);
    return action.id;
  }

  /**
   * Whether plan mode is currently on, derived from the most recent
   * enter_plan_mode / exit_plan_mode action. Defaults to `false`.
   *
   * Synchronous (unlike {@link mode}) so skills' `shouldInclude` can read it. Plan
   * mode is not inherited from a parent conversation.
   */
  get planMode(): boolean {
    const planActions = this.actions
      .map((a, index) => ({ action: a, index }))
      .filter(
        ({ action }) =>
          action.type === 'enter_plan_mode' || action.type === 'exit_plan_mode'
      )
      .sort(
        (a, b) =>
          b.action.actionData.createdAt.getTime() -
            a.action.actionData.createdAt.getTime() || b.index - a.index
      );

    const mostRecent = planActions[0];
    if (mostRecent === undefined) {
      return false;
    }
    return mostRecent.action.type === 'enter_plan_mode';
  }

  /**
   * Get the currently focused agent ID, if any.
   * Derived from the most recent focus_agent or unfocus_agent action.
   * Uses createdAt with array index as tiebreaker for actions created in the same tick.
   */
  getFocusedAgentId(): string | null {
    const focusActions = this.actions
      .map((a, index) => ({ action: a, index }))
      .filter(
        ({ action }) =>
          action.isFocusAgentAction() || action.isUnfocusAgentAction()
      )
      .sort(
        (a, b) =>
          b.action.actionData.createdAt.getTime() -
            a.action.actionData.createdAt.getTime() || b.index - a.index
      );

    const mostRecent = focusActions[0];
    if (mostRecent === undefined) {
      return null;
    }
    if (mostRecent.action.isFocusAgentAction()) {
      return mostRecent.action.getAgentId();
    }
    return null;
  }

  /**
   * Focused agent for this chat, inheriting from the parent when local focus
   * actions are absent (sub-agent chats such as agent-editor).
   */
  async resolveFocusedAgentId(): Promise<string | null> {
    const local = this.getFocusedAgentId();
    if (local !== null) {
      return local;
    }
    if (this.parentConversation !== undefined) {
      const parent = await this.parentConversation.get();
      if (parent === null) {
        return null;
      }
      return parent.resolveFocusedAgentId();
    }
    return null;
  }

  /**
   * Add a tool to the loaded tools list if not already present.
   * Use this when a tool becomes available (e.g., after credentials are configured).   */
  addLoadedTool(toolKey: ToolKey): void {
    if (!this.loadedTools.some((t) => t.toString() === toolKey.toString())) {
      this.loadedTools.push(toolKey);
    }
  }

  /**
   * Bootstrap tools available from chat creation (e.g. agent editor tools on sub-agent chats).
   * Updates in-memory loaded tools and persists completed add_tool actions so reload via
   * {@link inferLoadedToolsFromActions} matches ATC / {@link getToolKeys} candidate resolution.
   */
  seedPersistedLoadedTools(toolKeys: readonly ToolKey[]): void {
    for (const toolKey of toolKeys) {
      this.addLoadedTool(toolKey);
      const keyStr = toolKey.toString();
      const alreadyPersisted = this.actions.some((action) => {
        if (!action.isAddToolAction()) {
          return false;
        }
        if (!action.isApproved() && !action.isCompleted()) {
          return false;
        }
        try {
          return (action as AddToolAction).getToolKey() === keyStr;
        } catch {
          return false;
        }
      });
      if (alreadyPersisted) {
        continue;
      }
      const actionId = this.createAddToolAction({ toolKey });
      const action = this.actions.find((a) => a.id === actionId);
      if (action !== undefined) {
        action.complete();
        this.markActionAsModified(actionId);
      }
    }
  }

  /**
   * Register ad-hoc client-implemented (interruptable) tool definitions on
   * this conversation. Persists completed `register_interruptable_tool`
   * actions; the latest action per toolKey wins when the loader resolves a
   * definition. Re-registering an identical definition is a no-op.
   */
  registerInterruptableTools(
    definitions: readonly InterruptableToolDefinition[]
  ): void {
    for (const definition of definitions) {
      const existing = this.latestRegisteredInterruptableToolDefinition(
        definition.toolKey
      );
      if (
        existing !== null &&
        objectHash(existing) === objectHash(definition)
      ) {
        continue;
      }
      const actionModel = RegisterInterruptableToolAction.create({
        orgId: this.orgId,
        actorId: this.getOwnerId(),
        chatId: this.conversationId,
        definition,
      });
      this.actions.push(actionModel);
      this.newActionIds.add(actionModel.id);
    }
  }

  /** Latest registered {@link InterruptableToolDefinition} for a toolKey, or null when never registered. */
  latestRegisteredInterruptableToolDefinition(
    toolKey: string
  ): InterruptableToolDefinition | null {
    const matching = this.actions
      .filter(
        (a): a is RegisterInterruptableToolAction =>
          a.isRegisterInterruptableToolAction() && a.isCompleted()
      )
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.tryGetDefinition())
      .filter(
        (def): def is InterruptableToolDefinition =>
          def !== null && def.toolKey === toolKey
      );
    return matching[matching.length - 1] ?? null;
  }

  // ============================================================================
  // Action Management Methods
  // ============================================================================

  /**
   * Find a tool call by toolCallId.
   * Returns an object with toolKey, input, and other tool call information.
   * Returns null if the tool call is not found.
   *
   * This method finds tool calls regardless of whether they have output or not.
   * This is important for denied tools, which have output added by completeToolCall.
   */
  findToolCall(toolCallId: string): ToolPart | null {
    // Find the tool call in messages
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }

      // First try to find tool call without output (for pending/approved tools)
      const toolCallPart = msgModel.findToolCallPartWithoutOutput(toolCallId);
      if (toolCallPart) {
        return toolCallPart;
      }

      // If not found, try to find tool call with output (for denied/completed tools)
      // This is necessary because denyToolAction calls completeToolCall which adds output
      const toolCallPartWithOutput = msgModel.findToolCallPart(toolCallId);
      if (toolCallPartWithOutput) {
        // Create a ToolPart from the raw part to extract toolKey and input
        return createToolPart(
          toolCallPartWithOutput as ToolUIPart,
          this.actions
        );
      }
    }

    return null;
  }

  /**
   * Find the tool-call id of a tool call for `toolKey` whose result artifact is
   * `artifactId`, or null. Used to locate a server-posted placeholder (e.g. a
   * file upload) so it can be updated in place — the caller knows the dataset,
   * not the tool-call id.
   */
  findToolCallIdByArtifact(toolKey: string, artifactId: string): string | null {
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }
      for (const part of msgModel.getToolParts()) {
        if (
          part.toolKey().toString() === toolKey &&
          part.getArtifact()?.artifactId === artifactId
        ) {
          return part.toolCallId;
        }
      }
    }
    return null;
  }

  /**
   * Find the position of the message containing a tool call.
   * Returns the position if found, undefined otherwise.
   *
   * This method finds tool calls regardless of whether they have output or not.
   * This is important for denied tools, which have output added by completeToolCall.
   */
  findToolCallPosition(toolCallId: string): number | undefined {
    // Find the message containing the tool call
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }

      // First try to find tool call without output (for pending/approved tools)
      const toolCallPart = msgModel.findToolCallPartWithoutOutput(toolCallId);
      if (toolCallPart) {
        const msgId = msgModel.id;
        if (msgId !== undefined) {
          return this.messagePositions.get(msgId);
        }
      }

      // If not found, try to find tool call with output (for denied/completed tools)
      // This is necessary because denyToolAction calls completeToolCall which adds output
      const toolCallPartWithOutput = msgModel.findToolCallPart(toolCallId);
      if (toolCallPartWithOutput) {
        const msgId = msgModel.id;
        if (msgId !== undefined) {
          return this.messagePositions.get(msgId);
        }
      }
    }

    return undefined;
  }

  /**
   * Find the position of the message before the one containing a tool call.
   * This is the position where stepChat was called that generated the tool call.
   * Returns the position if found, undefined otherwise.
   */
  findPositionBeforeToolCall(toolCallId: string): number | undefined {
    const toolCallPosition = this.findToolCallPosition(toolCallId);
    if (toolCallPosition === undefined) {
      return undefined;
    }

    // Find the maximum position that is less than the tool call position
    // This represents the position where stepChat was called
    const positions = Array.from(this.messagePositions.values());
    const positionsBefore = positions.filter((pos) => pos < toolCallPosition);
    if (positionsBefore.length === 0) {
      return undefined;
    }

    return Math.max(...positionsBefore);
  }

  /**
   * Request approval for a tool call.
   * Finds the tool call in the conversation and creates an approval action.
   * Throws an error if the tool call is not found.
   */
  requestToolApproval(toolCallId: string): string {
    const toolCall = this.findToolCall(toolCallId);
    if (!toolCall) {
      throw new Error(
        `Tool call with ID ${toolCallId} not found in conversation`
      );
    }

    return this.createToolApprovalAction({
      toolKey: toolCall.toolKey(),
      toolCallId,
      toolInput: toolCall.input,
    });
  }

  /**
   * Create a tool approval action for a tool call.
   * Called when a tool requires manual approval.
   */
  createToolApprovalAction(params: {
    toolKey: ToolKey;
    toolCallId: string;
    toolInput: Record<string, unknown>;
  }): string {
    const actionModel = ToolApprovalAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      toolKey: params.toolKey.toString(),
      toolCallId: params.toolCallId,
      toolInput: params.toolInput,
    });

    this.actions.push(actionModel);
    this.newActionIds.add(actionModel.id);

    // Mark the tool call as awaiting input
    this.markToolAsAwaitingInput(params.toolCallId);

    return actionModel.id;
  }

  /**
   * Get all pending tool approval actions.
   */
  getPendingToolApprovals(): ChatActionData[] {
    return this.actions
      .filter((a) => a.isPending() && a.isToolApprovalAction())
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.actionData);
  }

  /**
   * Get pending tool approvals filtered by tool key.
   */
  getPendingToolApprovalsByToolKey(toolKey: ToolKey): ChatActionData[] {
    const allPending = this.getPendingToolApprovals();
    return allPending.filter(
      (action) => action.data?.['toolKey'] === toolKey.toString()
    );
  }

  /**
   * Approve a specific tool approval action.
   */
  approveToolAction(actionId: string, approvedBy: string): void {
    const action = this.actions.find((a) => a.id === actionId);
    if (action) {
      action.approve(approvedBy);
      this.markActionAsModified(actionId);
    }
  }

  /**
   * Deny a specific tool approval action.
   */
  denyToolAction(actionId: string, deniedBy: string, reason?: string): void {
    const action = this.actions.find((a) => a.id === actionId);
    if (action) {
      action.deny(deniedBy, reason);
      this.markActionAsModified(actionId);

      // Find the tool call and mark it as denied
      if (action.isToolApprovalAction()) {
        const toolCallId = action.getToolCallId();
        Assert.assert(
          toolCallId !== undefined,
          'Tool call ID not found in tool approval action'
        );
        // Add error result for denied tool
        this.completeToolCall(toolCallId, {
          type: 'error',
          message: reason ?? 'Tool execution denied by user',
        });
      }
    }
  }

  /**
   * Approve all pending tool approvals for a specific tool type.
   */
  approveAllToolApprovalsByType(toolKey: ToolKey, approvedBy: string): number {
    let count = 0;

    for (const action of this.actions) {
      if (
        action.isToolApprovalAction() &&
        action.isPending() &&
        action.getToolKey() === toolKey.toString()
      ) {
        action.approve(approvedBy);
        this.markActionAsModified(action.id);
        count++;
      }
    }

    return count;
  }

  /**
   * Approve all pending tool approvals in the conversation.
   */
  approveAllToolApprovals(approvedBy: string): number {
    let count = 0;

    for (const action of this.actions) {
      if (action.isToolApprovalAction() && action.isPending()) {
        action.approve(approvedBy);
        this.markActionAsModified(action.id);
        count++;
      }
    }

    return count;
  }

  /**
   * Check if a tool call has been approved.
   */
  async isToolCallApproved(toolCallId: string): Promise<boolean> {
    const action = this.actions
      .filter(
        (a) => a.isToolApprovalAction() && a.getToolCallId() === toolCallId
      )
      .sort(
        (a, b) =>
          b.actionData.createdAt.getTime() - a.actionData.createdAt.getTime()
      )[0] as ToolApprovalAction | undefined;

    return action?.isApproved() ?? (await this.mode()) === 'auto_approve';
  }

  /**
   * Status of the most recent tool approval action for a tool call, regardless
   * of whether it is still pending. Returns undefined when no approval action
   * exists for the call. Used to make approve/deny idempotent — a repeated
   * request for an already-resolved call can be detected instead of 404ing.
   */
  getToolApprovalStatus(toolCallId: string): ChatActionStatus | undefined {
    const action = this.actions
      .filter(
        (a) => a.isToolApprovalAction() && a.getToolCallId() === toolCallId
      )
      .sort(
        (a, b) =>
          b.actionData.createdAt.getTime() - a.actionData.createdAt.getTime()
      )[0];

    return action?.status;
  }

  /**
   * True when the user explicitly approved this tool call via a completed tool_approval action.
   * Does not treat auto_approve mode as approval — that only skips the approval gate before first run.
   */
  hasExplicitToolCallApproval(toolCallId: string): boolean {
    const action = this.actions
      .filter(
        (a) => a.isToolApprovalAction() && a.getToolCallId() === toolCallId
      )
      .sort(
        (a, b) =>
          b.actionData.createdAt.getTime() - a.actionData.createdAt.getTime()
      )[0] as ToolApprovalAction | undefined;

    return action?.isApproved() ?? false;
  }

  /**
   * True when a tool_approval action for this call is still pending (user has not approved/denied yet).
   */
  private toolCallHasPendingApproval(toolCallId: string): boolean {
    return this.actions.some(
      (a) =>
        a.isToolApprovalAction() &&
        a.getToolCallId() === toolCallId &&
        a.isPending()
    );
  }

  /**
   * Whether an incomplete tool part (already has output) should run again after user action.
   *
   * - Interrupted user data is enough for interruptable tools (unless a tool_approval is still
   *   pending — then require an explicit approve).
   * - Otherwise re-run only when there is an approved tool_approval for this call.
   *
   * Async waits (workflow/sub-agent spawn) never create a tool_approval — they stay incomplete
   * until completed externally, so {@link hasExplicitToolCallApproval} stays false.
   */
  private incompleteToolNeedsReexecution(toolPart: ToolPart): boolean {
    const toolCallId = toolPart.toolCallId;
    const explicitlyApproved = this.hasExplicitToolCallApproval(toolCallId);

    if (this.hasInterruptedToolDataProvided(toolCallId)) {
      if (this.toolCallHasPendingApproval(toolCallId)) {
        return explicitlyApproved;
      }
      return true;
    }

    return explicitlyApproved;
  }

  /**
   * True when the user submitted data for an interruptable tool (see runInterruptedTool).
   */
  hasInterruptedToolDataProvided(toolCallId: string): boolean {
    return this.getActions({
      type: ChatActionType.InterruptedToolDataProvided,
    }).some((action) => action.data?.['toolCallId'] === toolCallId);
  }

  /**
   * Whether an assistant tool part should be executed (or re-executed) by the agent loop.
   *
   * - {@link ToolPart.needsExecution}: never ran (input-available, no output) — execute unless a
   *   tool_approval is still pending.
   * - Incomplete with output: see {@link incompleteToolNeedsReexecution} — interrupted user data
   *   or an approved tool_approval (async waits without a tool_approval do not re-run).
   */
  async shouldReexecuteToolPart(toolPart: ToolPart): Promise<boolean> {
    if (toolPart.needsExecution()) {
      // Never ran, but a pending approval action means we are waiting on the
      // user (manual mode) — do not execute until it is explicitly approved.
      return !this.toolCallHasPendingApproval(toolPart.toolCallId);
    }
    if (!toolPart.isIncomplete()) {
      return false;
    }
    return this.incompleteToolNeedsReexecution(toolPart);
  }

  /**
   * True when the tool call is in input-available state with user-provided input
   * (e.g. after runInterruptedTool). Execution is allowed without an approval action.
   */
  isToolCallHavingUserInput(toolCallId: string): boolean {
    for (const msgModel of this.messages) {
      if (!msgModel?.isAssistantMessage()) {
        continue;
      }
      const part = msgModel.findToolCallPart(toolCallId);
      if (part === undefined) {
        continue;
      }
      const withState = part as { state?: string };
      return withState.state === 'input-available';
    }
    return false;
  }

  /**
   * Queue a message for later sending.
   */
  queueMessage(params: { content: string; files?: unknown[] }): string {
    const actionModel = MessageQueueAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      messageContent: params.content,
      messageFiles: params.files,
    });

    this.actions.push(actionModel);
    this.newActionIds.add(actionModel.id);

    return actionModel.id;
  }

  /**
   * Get all queued messages.
   */
  getQueuedMessages(): ChatActionData[] {
    return this.actions
      .filter((a) => a.isPending() && a.isMessageQueueAction())
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.actionData);
  }

  /**
   * Pop a queued message and add it to the conversation.
   * Throws an error if canPassToLLM() is false.
   */
  popQueuedMessage(actionId: string): void {
    if (!this.shouldYieldControlBackToTheLlm()) {
      throw new Error(
        'Cannot pop queued message: conversation has pending tool calls'
      );
    }

    const action = this.actions.find(
      (a) => a.id === actionId && a.isMessageQueueAction() && a.isPending()
    );

    if (!action || !action.isMessageQueueAction()) {
      throw new Error(`Queued message action ${actionId} not found`);
    }

    const messageContent = action.getMessageContent();
    const messageFiles = action.getMessageFiles();

    // Build the UIMessage parts array
    const parts: UIMessage['parts'] = [];

    // Add files first if they exist
    if (messageFiles && messageFiles.length > 0) {
      parts.push(...(messageFiles as UIMessage['parts']));
    }

    // Add text content if it exists
    if (messageContent) {
      parts.push({
        type: 'text',
        text: messageContent,
      });
    }

    // Create the user message
    const uiMessage: UIMessage = {
      id: generateMessageId(),
      role: 'user',
      parts,
    };

    // Add the message to the conversation
    this.addMessage(uiMessage);

    // Mark the action as completed
    action.complete();
    this.markActionAsModified(actionId);
  }

  /**
   * Create an add_tool action to request loading/adding a tool.
   */
  createAddToolAction(params: {
    toolKey: ToolKey;
    toolCallId?: string;
  }): string {
    const actionModel = AddToolAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      toolKey: params.toolKey.toString(),
      toolCallId: params.toolCallId,
    });

    this.actions.push(actionModel);
    this.newActionIds.add(actionModel.id);

    return actionModel.id;
  }

  /**
   * Create a remove_tool action to request unloading/removing a tool.
   * @param immediate - When true, creates a completed action that takes effect immediately (e.g. user-initiated unload).
   */
  createRemoveToolAction(params: {
    toolKey: ToolKey;
    reason?: string;
    immediate?: boolean;
  }): string {
    const toolKeyStr = params.toolKey.toString();
    const actionModel = RemoveToolAction.create({
      orgId: this.orgId,
      actorId: this.getOwnerId(),
      chatId: this.conversationId,
      toolKey: toolKeyStr,
      reason: params.reason,
      status: params.immediate === true ? 'completed' : 'pending',
    });

    this.actions.push(actionModel);
    this.newActionIds.add(actionModel.id);

    if (params.immediate === true) {
      this.loadedTools = this.loadedTools.filter(
        (t) => t.toString() !== toolKeyStr
      );
    }

    return actionModel.id;
  }

  /**
   * Get all pending add_tool actions.
   */
  getPendingAddToolActions(): ChatActionData[] {
    return this.actions
      .filter((a) => a.isPending() && a.isAddToolAction())
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.actionData);
  }

  /**
   * Get all pending remove_tool actions.
   */
  getPendingRemoveToolActions(): ChatActionData[] {
    return this.actions
      .filter((a) => a.isPending() && a.isRemoveToolAction())
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.actionData);
  }

  /**
   * Get all actions, optionally filtered by type and status.
   */
  getActions(params?: {
    type?: ChatActionType;
    status?: ChatActionStatus;
  }): ChatActionData[] {
    let filtered = this.actions;
    if (params?.type !== undefined) {
      filtered = filtered.filter((a) => a.type === params.type);
    }
    if (params?.status !== undefined) {
      filtered = filtered.filter((a) => a.status === params.status);
    }
    return filtered
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      )
      .map((a) => a.actionData);
  }

  /**
   * Add multiple UI messages to the conversation.
   * Automatically filters out messages that already exist to prevent duplicates.
   * Marks all added messages as new.
   * Assigns positions to new messages so they're included in getMessagesAfterCompaction().   */
  addMessages(uiMessages: UIMessage[]): void {
    // Filter out messages that already exist in the conversation to prevent duplicates
    const existingMessageIds = new Set(
      this.messages
        .map((msg) => msg.id)
        .filter((id): id is string => id !== undefined)
    );

    const newMessages = uiMessages.filter(
      (msg) => msg.id === undefined || !existingMessageIds.has(msg.id)
    );

    // If no new messages, return early
    if (newMessages.length === 0) {
      return;
    }

    const messagesToAdd: MessageClass[] = newMessages.map((uiMessage) =>
      MessageClass.fromUIMessage(
        this.conversationId,
        uiMessage,
        'chat',
        this.actions
      )
    );

    this.messages.push(...messagesToAdd);

    // Calculate the maximum existing position to assign positions to new messages
    // This ensures new messages are included in getMessagesAfterCompaction() when compaction history exists
    const maxPosition =
      this.messagePositions.size > 0
        ? Math.max(...Array.from(this.messagePositions.values()))
        : -1;

    // Mark all new messages and assign positions
    let nextPosition = maxPosition + 1;
    for (const msgModel of messagesToAdd) {
      if (msgModel.id !== undefined) {
        this.newMessageIds.add(msgModel.id);
        // Assign a position so the message is included in getMessagesAfterCompaction()
        this.messagePositions.set(msgModel.id, nextPosition);
        nextPosition++;
      }
    }
  }

  /**
   * Create a user message with plain text content.
   * Helper method for creating simple text user messages.
   */
  static createTextUserMessage(text: string, messageId?: string): UIMessage {
    return {
      id: messageId ?? generateMessageId(),
      role: 'user',
      parts: [
        {
          type: 'text',
          text,
        },
      ],
    };
  }

  /**
   * Create a user message with TipTap editor state.
   * Helper method for creating rich user messages with editor state.
   */
  static createRichTextUserMessage(
    editorState: {
      type: 'doc';
      content?: Array<{
        type: string;
        attrs?: Record<string, any>;
        content?: any[];
        marks?: any[];
      }>;
    },
    messageId?: string
  ): UIMessage {
    return {
      id: messageId ?? generateMessageId(),
      role: 'user',
      parts: [
        {
          type: 'editor-state' as any,
          editorState,
        } as any,
      ],
    };
  }

  /**
   * Add a single UI message to the conversation.
   * Automatically filters out messages that already exist to prevent duplicates.
   * Deduplicates tool call parts within the message to prevent duplicate tool calls.
   * Marks the message as new.
   * Assigns a position to the new message so it's included in getMessagesAfterCompaction().   */
  setAssistantMessageLlmUsage(
    messageId: string,
    usage: AssistantMessageLlmUsage
  ): void {
    const message = this.messages.find((msg) => msg.id === messageId);
    if (message === undefined || !message.isAssistantMessage()) {
      return;
    }

    message.setLlmUsage(usage);
    if (this.initialMessageIds.has(messageId)) {
      this.modifiedMessageIds.add(messageId);
      this.newMessageIds.delete(messageId);
    }
  }

  addMessage(
    uiMessage: UIMessage,
    type: 'chat' | 'compaction' | 'hidden' = 'chat'
  ): void {
    if (uiMessage.id !== undefined) {
      const existingMessageIds = new Set(
        this.messages
          .map((msg) => msg.id)
          .filter((id): id is string => id !== undefined)
      );

      if (existingMessageIds.has(uiMessage.id)) {
        return;
      }
    }

    // Create MessageClass (which handles deduplication) using factory method
    const msgModel = MessageClass.fromUIMessage(
      this.conversationId,
      uiMessage,
      type,
      this.actions
    );
    this.messages.push(msgModel);

    if (msgModel.id !== undefined) {
      this.newMessageIds.add(msgModel.id);
      // Calculate the maximum existing position to assign a position to the new message
      // This ensures new messages are included in getMessagesAfterCompaction() when compaction history exists
      const maxPosition =
        this.messagePositions.size > 0
          ? Math.max(...Array.from(this.messagePositions.values()))
          : -1;
      const nextPosition = maxPosition + 1;
      this.messagePositions.set(msgModel.id, nextPosition);
    }
  }

  // ============================================================================
  // Non-Chainable Methods (return other types)
  // ============================================================================

  /**
   * Get model messages for the LLM prompt (compaction window, tool pruning, dynamic tail).
   *
   * Message selection runs in {@link ConversationRenderer}; this method wires
   * {@link toLlmInput} → {@link LLMInputBuilder.build} with {@link ConversationRenderer}.
   */
  async getModelMessages(
    opts: ConversationRendererOptions
  ): Promise<ModelMessage[]> {
    const chatModel = await this.model();
    const modelId = chatModel?.model ?? coco.DEFAULT_MODEL;
    const builder = (await this.toLlmInput(opts)).model(
      modelAdapterFor(modelId)
    );
    const { llmInput } = await builder.build(
      (b) => new ConversationRenderer(b)
    );
    return llmInput.messages;
  }

  /** Populate {@link LLMInputBuilder} with conversation data for {@link ConversationRenderer}. */
  private async prepareConversationPromptBuilder(
    opts: ConversationToLlmInputOptions
  ): Promise<LLMInputBuilder> {
    const promptOptions: ConversationPromptOptions = {
      workspaceDomainLoader: this.workspaceDomainLoader,
      prune: opts.prune,
      participantContext: opts.participantContext,
    };
    const toolKeys = await this.getToolKeys();
    const promptMessages = this.messages.filter(
      (msg) => !SystemMessageClass.isConversationSystemStorage(msg)
    );
    return llmInputBuilder()
      .withMessages(promptMessages)
      .withConversationPromptOptions(
        toLlmConversationPromptOptions(promptOptions)
      )
      .withResolvedToolKeys([...toolKeys])
      .withTrigger(this.triggeredBy());
  }

  /** @internal For {@link ConversationRenderer}. */
  getWorkspaceDomainLoader(): WorkspaceDomainLoader | undefined {
    return this.workspaceDomainLoader;
  }

  /**
   * Durable user/org facts for the LLM prompt when injection gates pass
   * ({@link shouldInjectLearnedFactsForPrompt}). Returns `[]` for sub-agents,
   * warehouse/compaction-summary runs, chats without the workspace tool, or when
   * no {@link ConversationParams.learnedFactsLoader} was provided.
   */
  async facts(actorId: string): Promise<Fact[]> {
    if (this.learnedFactsLoader === undefined) {
      return [];
    }
    const toolKeys = await this.getToolKeys();
    if (
      !shouldInjectLearnedFactsForPrompt({
        parentConversationId: this.parent(),
        trigger: this.triggeredBy(),
        loadedToolKeys: toolKeys,
      })
    ) {
      return [];
    }
    return [...(await this.learnedFactsLoader(actorId))];
  }

  /**
   * Stored receipts for previously-completed tool calls in this chat,
   * substituted in place of raw tool outputs by {@link ConversationRenderer}.
   * Returns `[]` when no {@link ConversationParams.toolReceiptsLoader} was
   * provided (tests, non-Coco chats).
   */
  async toolReceipts(): Promise<Receipt[]> {
    if (this.toolReceiptsLoader === undefined) {
      return [];
    }
    return [...(await this.toolReceiptsLoader())];
  }

  /**
   * The chat's ingot tables for the `<ingot_schema>` prompt block. Null when no
   * {@link ConversationParams.ingotSchemaLoader} was provided, when
   * `coco/ingot-query` isn't loaded (nothing could query them), or on
   * compaction-summary runs.
   */
  async ingotSchema(): Promise<IngotSchema | null> {
    if (this.ingotSchemaLoader === undefined) {
      return null;
    }
    if (this.triggeredBy() === 'compaction-summary') {
      return null;
    }
    const toolKeys = await this.getToolKeys();
    if (!toolKeys.includes(COCO_INGOT_QUERY_TOOL_KEY)) {
      return null;
    }
    return await this.ingotSchemaLoader();
  }

  /**
   * Distinct authoring actor ids across user messages. More than one means a
   * group chat; callers use this to build {@link ConversationRendererOptions.participantContext}.
   * Empty for messages not hydrated from persisted rows (no `actor_id`).
   */
  getUserMessageSenderActorIds(): string[] {
    const ids = new Set<string>();
    for (const msg of this.messages) {
      if (msg.role !== 'user') {
        continue;
      }
      const senderId = msg.senderActorId;
      if (senderId !== undefined && senderId !== '') {
        ids.add(senderId);
      }
    }
    return [...ids];
  }

  /**
   * Configure an {@link LLMInputBuilder} for this conversation. Call
   * {@link LLMInputBuilder.model} then {@link LLMInputBuilder.build} with
   * {@link ConversationRenderer}.
   */
  async toLlmInput(
    opts: ConversationToLlmInputOptions
  ): Promise<LLMInputBuilder> {
    this.promptRenderOptions = opts;
    try {
      let builder = await this.prepareConversationPromptBuilder(opts);

      if (opts.comprehensionEncoding !== undefined) {
        builder = builder.withComprehensionEncoding(opts.comprehensionEncoding);
      }

      builder = builder.withSystem(this.resolveLlmSystemMessage(opts));

      if (opts.callSettings !== undefined) {
        builder = builder.withCallSettings(opts.callSettings);
      }

      if (opts.streamTextOutput !== undefined) {
        builder = builder.withStreamTextOutput(opts.streamTextOutput);
      }

      if (opts.outputShape !== undefined) {
        builder = builder.withOutputShape(opts.outputShape);
      }

      const facts = await this.facts(opts.actorId);
      if (facts.length > 0) {
        builder = builder.withFacts(facts);
      }

      const receipts = await this.toolReceipts();
      if (receipts.length > 0) {
        builder = builder.withReceipts(receipts);
      }

      // Reloaded every step, so a table a tool wrote earlier in the turn shows
      // up here. With no tables there is nothing to query, so the tool is
      // withheld for this step: an empty-schema note alone didn't stop models
      // from querying tables they imagined.
      const ingotSchema = await this.ingotSchema();
      if (ingotSchema !== null) {
        builder =
          ingotSchema.tables.length === 0
            ? builder.withResolvedToolKeys(
                builder
                  .getResolvedToolKeys()
                  .filter((key) => key !== COCO_INGOT_QUERY_TOOL_KEY)
              )
            : builder.withIngotSchema(ingotSchema);
      }

      for (const skill of await this.resolveSkillsToInject()) {
        if (skill.instructions.trim() === '') {
          continue;
        }
        builder = builder.withSkill({
          name: skill.displayName,
          instructions: skill.instructions,
        });
      }

      return builder;
    } finally {
      this.promptRenderOptions = undefined;
    }
  }

  /**
   * Whether any user message in this conversation has image parts (UI `image`
   * or `file` with an `image/*` media type).
   */
  hasImagesInMessages(): boolean {
    return this.messages.some(
      (msg) =>
        uiMessageHasImageParts(msg.uiMessage) ||
        uiMessageHasToolImageParts(msg.uiMessage)
    );
  }

  /**
   * Image parts (`image` / `file` with an `image/*` media type) from the most
   * recent visible user message. Used to seed a delegated sub-agent with the
   * images the user just shared — delegation otherwise flattens the turn to a
   * text prompt and drops them. Returns the images from that one turn only (not
   * the whole history), or `[]` when the latest user message had none.
   */
  latestUserImageParts(): UIMessage['parts'] {
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      const message = this.messages[i];
      if (
        message === undefined ||
        !message.isUserMessage() ||
        message.isHidden()
      ) {
        continue;
      }
      return (message.uiMessage.parts ?? []).filter((part) =>
        rawMessagePartIsImage(part)
      );
    }
    return [];
  }

  /**
   * Generate a hash of the UI messages (excluding message IDs).
   * Uses the same logic as objectHash(uiMessages.map((m) => omit(m, 'id'))) from step-chat.ts.   */
  hash(): string {
    const uiMessages = this.messages.map((m) => m.uiMessage);
    return objectHash(uiMessages.map((m) => omit(m, 'id')));
  }

  /**
   * Get all MessageClass instances for the conversation.
   */
  getAllMessages(): MessageClass[] {
    return [...this.messages];
  }

  /**
   * Text from visible user messages, ordered oldest to newest.
   * Includes plain text parts and text inside TipTap editor-state parts.
   */
  userMessages(): string[] {
    return this.messages
      .filter((message) => message.isUserMessage() && !message.isHidden())
      .map((message) => message.toText());
  }

  /**
   * Workspace resource @mentions (agents, datasets, documents, workflows) in this
   * chat's user messages, deduped by `type:id`.
   *
   * A mention chip renders as empty text — "attach @agent to @dataset" carries no
   * "agent"/"dataset" word in {@link Conversation.userMessages} at all — so a
   * skill gate that only reads the message text cannot see the two resources the
   * user connected, and a @mentioned dataset is never an "open" artifact either.
   * This exposes the referenced resources by type instead, which is how the
   * "attach an agent to a dataset" intent is recognised.
   */
  mentionedResourceRefs(): WorkspaceResourceRef[] {
    const byKey = new Map<string, WorkspaceResourceRef>();
    for (const message of this.messages) {
      if (!message.isUserMessage()) {
        continue;
      }
      for (const part of message.uiMessage.parts ?? []) {
        const p = part as { type?: string; editorState?: unknown };
        if (
          p.type !== 'editor-state' ||
          p.editorState === undefined ||
          p.editorState === null
        ) {
          continue;
        }
        for (const ref of collectWorkspaceResourceRefs(
          p.editorState as DocumentState
        )) {
          byKey.set(`${ref.resourceType}:${ref.resourceId}`, ref);
        }
      }
    }
    return [...byKey.values()];
  }

  /**
   * Get all MessageClass instances for the conversation.
   * Use this when you need to work with the message models directly.
   */
  getMessageInstances(): MessageClass[] {
    return [...this.messages];
  }

  /**
   * Get MessageClass instances up to a specific position (inclusive).
   * If no position is specified, returns all messages.   */
  getMessagesUpToPosition(upToPosition?: number): MessageClass[] {
    if (upToPosition === undefined) {
      return this.getAllMessages();
    }

    return this.messages.filter((msgModel) => {
      const msgId = msgModel.id;
      if (msgId === undefined) {
        return false;
      }
      const position = this.messagePositions.get(msgId);
      return position !== undefined && position <= upToPosition;
    });
  }

  /**
   * Get the result of the conversation — the last assistant message with at least one part.
   * Skips trailing empty assistant rows (streaming placeholders) so sub-agent handoff
   * serializes the substantive reply (often a hidden summary) instead of an empty tail.
   */
  result(): AssistantMessage | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const msg = this.messages[i];
      if (!msg?.isAssistantMessage()) {
        continue;
      }
      const parts = msg.raw().parts ?? [];
      if (parts.length === 0) {
        continue;
      }
      return msg;
    }
    return null;
  }

  /**
   * Returns the latest assistant output from the last message in the conversation.
   * Delegates parsing to AssistantMessageClass.getOutput().
   */
  getOutput(): JsonValue | string | null {
    const lastMessage = this.messages[this.messages.length - 1];
    if (lastMessage === undefined || !lastMessage.isAssistantMessage()) {
      return null;
    }
    return lastMessage.getOutput();
  }

  /**
   * Get a message at a specific position.
   * Returns undefined if no message is found at that position.   */
  getMessageAtPosition(position: number): MessageClass | undefined {
    return this.messages.find((msgModel) => {
      const msgId = msgModel.id;
      if (msgId === undefined) {
        return false;
      }
      const msgPosition = this.messagePositions.get(msgId);
      return msgPosition === position;
    });
  }

  /**
   * Get a message by its ID. Returns undefined if not found.
   */
  getMessageById(messageId: string): MessageClass | undefined {
    return this.messages.find((msgModel) => msgModel.id === messageId);
  }

  /**
   * Tools explicitly tracked on the conversation plus keys inferred from the current message
   * list (tool parts, coco/load-resource targets, @mentions in editor-state and nested
   * documents when {@link ConversationParams.referencedDocumentsById} is provided),
   * plus tool/agent @mentions in a {@link ConversationParams.linkedAgent} workspace prompt,
   * with remove_tool filtering applied.
   *
   * This powers "loaded in thread" state. It is intentionally broader than constructor-provided
   * tools and is used for context/diagnostics. It is not the final LLM/runtime allowlist.
   */
  private resolveLoadedToolsMergedWithHistory(): ToolKey[] {
    const merged = new Set<string>();
    for (const t of this.loadedTools) {
      merged.add(t.toString());
    }
    for (const t of this.inferLoadedToolsFromHistory()) {
      merged.add(t.toString());
    }
    for (const t of this.inferLoadedToolsFromActions()) {
      merged.add(t.toString());
    }
    for (const key of this.inferToolKeysFromLinkedAgent()) {
      merged.add(key);
    }
    const focusedAgentId = this.getFocusedAgentId();
    if (focusedAgentId !== null) {
      merged.add(agentIdToAgentToolKey(focusedAgentId));
    }
    return this.filterRemovedTools(
      new Set(this.withoutLinkedAgentSelfToolKey([...merged]))
    );
  }

  /**
   * Tool keys from @mentions in a linked saved agent's TipTap system prompt
   * ({@link Agent#editorState}). Does not include the linked agent's own
   * `coco/agent-…` key — the agent is already the system prompt for this chat,
   * so exposing it as a tool causes pointless self-delegation as a sub-agent.
   */
  private inferToolKeysFromLinkedAgent(): string[] {
    const linkedAgent = this.getLinkedAgent();
    if (linkedAgent === null) {
      return [];
    }
    const selfToolKey = agentIdToAgentToolKey(linkedAgent.id);
    const keys = new Set<string>();
    for (const key of linkedAgent.editorState.tools()) {
      try {
        const normalized = ToolKey.fromString(key).toString();
        if (normalized === selfToolKey) {
          continue;
        }
        keys.add(normalized);
      } catch {
        // ignore invalid tool keys
      }
    }
    return [...keys];
  }

  /**
   * Remove the linked agent's own invocable tool key from an LLM tool
   * allowlist. A chat linked to an agent must never offer that agent to
   * itself: models treat the matching tool as a delegation target and re-run
   * the whole agent nested inside its own conversation (double cost, and the
   * real answer strands in the child chat). The Coco sub-agent path gets the
   * same guarantee via `excludedToolKeys` (see CoteraAgentTool.excludedTools).
   */
  protected withoutLinkedAgentSelfTool(
    keys: readonly string[]
  ): readonly string[] {
    const linkedAgent = this.getLinkedAgent();
    if (linkedAgent === null) {
      return keys;
    }
    const selfKey = agentIdToAgentToolKey(linkedAgent.id);
    return keys.filter((key) => key !== selfKey);
  }

  /**
   * Tools inferred from persisted state, history, and actions only.
   *
   * Use this for "what appears loaded in this thread right now" views.
   * For the LLM prompt / execution allowlist, always use {@link getToolKeys}.
   */
  getLoadedTools(): ToolKey[] {
    return this.resolveLoadedToolsMergedWithHistory();
  }

  /**
   * Tools explicitly provided in the constructor (`params.loadedTools`) only.
   *
   * This is used for ownership/bootstrap semantics:
   * - deciding whether a tool should run as the linked-agent owner vs current actor
   * - identifying the initial included set before dynamic load actions/messages
   *
   * It intentionally excludes tools dynamically loaded later in the thread.
   * Do not use this as the prompt/runtime allowlist (use {@link getToolKeys}).
   */
  getIncludedTools(): ToolKey[] {
    const dynamicallyLoadedToolKeys = this.inferDynamicallyLoadedToolKeys();
    return uniqBy(
      [...this.includedTools].filter(
        (tool) => !dynamicallyLoadedToolKeys.has(tool.toString())
      ),
      (t) => t.toString()
    );
  }

  /**
   * Build the system message: base + optional suffix.
   *
   * Per-turn autoload Coco skills are injected via {@link SkillLoader} in
   * {@link toLlmInput} (dynamic block before the latest user message).
   */
  static buildSystemMessage(base: string, suffix?: string): string {
    return suffix !== undefined && suffix !== ''
      ? `${base}\n\n${suffix}`
      : base;
  }

  /**
   * Get the base system message (base + optional suffix)
   */
  getSystemMessage(suffix?: string): string {
    return Conversation.buildSystemMessage(
      this.systemMessage,
      suffix !== undefined && suffix !== '' ? suffix : undefined
    );
  }

  /**
   * Resolves streamText `system` for {@link toLlmInput}: optional override, linked-agent
   * editor-state, persisted chat_messages row, or coco default.
   */
  protected resolveLlmSystemMessage(
    opts: Pick<ConversationToLlmInputOptions, 'system'> = {}
  ): SystemMessageClass {
    if (opts.system !== undefined) {
      return opts.system;
    }
    const linkedAgentSystem = this.resolveLinkedAgentSystemMessage();
    if (linkedAgentSystem !== undefined) {
      return linkedAgentSystem;
    }
    const stored = this.getStoredConversationSystemMessage();
    if (stored !== undefined) {
      return stored;
    }
    return SystemMessageClass.coco(this.conversationId);
  }

  /**
   * Persisted system row for non-agent chats (`chat_messages`, type `hidden`).
   */
  getStoredConversationSystemMessage(): SystemMessageClass | undefined {
    for (const msg of this.messages) {
      if (SystemMessageClass.isConversationSystemStorage(msg)) {
        return msg as SystemMessageClass;
      }
    }
    return undefined;
  }

  /**
   * True when this chat's system prompt was replaced by a caller-supplied
   * prompt — the persisted stored-system row is `ad-hoc` (e.g. a chat POST with
   * `overrideSystemPrompt`, or a Claude Code / gateway session that seeded its
   * own system) rather than the default `coco` identity.
   *
   * Such chats are "clean-room": the caller intentionally swapped out Coco, so
   * we do not want the autoloaded Coco registry skills (and their bundled tools)
   * silently reintroducing the Coco persona and Cotera-specific guidance on top
   * of the custom prompt — see {@link getPromptInjectSkills}. Coco-default
   * chats (`{t:'coco'}`) and append-mode chats (coco stored + an extra system
   * message) keep the skills.
   */
  hasCustomSystemPromptOverride(): boolean {
    const stored = this.getStoredConversationSystemMessage();
    if (stored === undefined) {
      return false;
    }
    return stored.getStoredPayload()?.t === 'ad-hoc';
  }

  /**
   * Build a stored system payload from a legacy string (e.g. saga `systemMessage` param).
   */
  static storedSystemPayloadFromString(
    systemMessage: string
  ): StoredSystemMessagePayload {
    if (systemMessage.trim() === coco.SYSTEM_MESSAGE.trim()) {
      return { t: 'coco' };
    }
    return { t: 'ad-hoc', message: systemMessage };
  }

  /**
   * System prompt for agent-linked sub-chats: TipTap editor-state resolved at LLM build time.
   */
  private resolveLinkedAgentSystemMessage(): SystemMessageClass | undefined {
    const agent = this.getLinkedAgent();
    if (agent === null || !this.runsAsLinkedAgent()) {
      return undefined;
    }
    return SystemMessageClass.forLinkedAgent(this.conversationId, agent, {
      parentId: this.chatModel?.parentId,
    });
  }

  private collectDocumentReferenceIds(params?: {
    suffix?: string | undefined;
    loadedAgentContents?: LoadedAgentContent[];
  }): string[] {
    const ids = new Set<string>();
    const addFromText = (text: string | undefined): void => {
      if (text === undefined || text.trim().length === 0) {
        return;
      }
      const pattern = new RegExp(DOCUMENT_REFERENCE_ID_PATTERN.source, 'gi');
      for (const match of text.matchAll(pattern)) {
        const id = match[1];
        if (id !== undefined && id.length > 0) {
          ids.add(id);
        }
      }
    };

    addFromText(this.systemMessage);
    addFromText(params?.suffix);
    for (const loaded of params?.loadedAgentContents ?? []) {
      addFromText(loaded.systemPrompt);
    }

    const linkedAgent = this.getLinkedAgent();
    if (linkedAgent !== null) {
      const refs = collectWorkspaceResourceRefs(
        linkedAgent.editorState.getState()
      );
      for (const ref of refs) {
        if (ref.resourceType === ResourceType.Document) {
          ids.add(ref.resourceId);
        }
      }
    }

    for (const [id] of this.referencedDocumentsById ?? []) {
      ids.add(id);
    }

    for (const msgModel of this.messages) {
      for (const part of msgModel.uiMessage.parts ?? []) {
        const p = part as {
          type?: string;
          text?: unknown;
          editorState?: unknown;
        };
        if (p.type === 'text' && typeof p.text === 'string') {
          addFromText(p.text);
        }
        if (
          p.type === 'editor-state' &&
          p.editorState !== undefined &&
          p.editorState !== null
        ) {
          const refs = collectWorkspaceResourceRefs(
            p.editorState as DocumentState
          );
          for (const ref of refs) {
            if (ref.resourceType === ResourceType.Document) {
              ids.add(ref.resourceId);
            }
          }
        }
      }
    }

    return [...ids];
  }

  /**
   * Flattened artifact descriptors from direct `ConversationParams.children` only.
   * Each child contributes the result of its `artifacts()` implementation (recursive per child).
   * Collapses duplicate {@link ArtifactDescriptor.artifactId} across siblings (last child wins).
   */
  private async collectDescendantArtifactDescriptors(): Promise<
    ArtifactDescriptor[]
  > {
    const byId = new Map<string, ArtifactDescriptor>();
    for (const child of this.children) {
      for (const d of await child.artifacts()) {
        Conversation.mergeArtifactByArtifactId(byId, d);
      }
    }
    return Array.from(byId.values());
  }

  private mergeParentOwnChatArtifactsInto(
    byArtifactId: Map<string, ArtifactDescriptor>,
    parentConv: Conversation | null
  ): void {
    if (parentConv === null) {
      return;
    }
    for (const d of parentConv.ownChatArtifacts()) {
      Conversation.mergeArtifactByArtifactId(byArtifactId, d);
    }
  }

  /**
   * Deduplicates artifacts in a map keyed **only** by {@link ArtifactDescriptor.artifactId}
   * (not payload fields). Later entries replace earlier ones for the same id,
   * except for `origin` — see {@link Conversation.latestArtifactOrigin}.
   */
  private static mergeArtifactByArtifactId(
    map: Map<string, ArtifactDescriptor>,
    descriptor: ArtifactDescriptor
  ): void {
    const existing = map.get(descriptor.artifactId);
    if (existing === undefined) {
      map.set(descriptor.artifactId, descriptor);
      return;
    }
    map.set(descriptor.artifactId, {
      ...descriptor,
      origin: Conversation.latestArtifactOrigin(
        existing.origin,
        descriptor.origin
      ),
    });
  }

  /**
   * Which of two accounts of the same artifact to keep.
   *
   * Plain last-wins is right for the rest of a descriptor — a later tool output
   * carries the fresher payload — but cannot be applied to `origin`, because
   * the sources being merged are *different chats* rather than successive
   * versions of one fact, and they arrive in a fixed source order (parent, own,
   * descendants) that has nothing to do with when anything happened.
   *
   * So origin is a union, and `edited` outranks `created`: the row reports the
   * last thing that was done to the artifact, which is what someone scanning
   * the index after a working session is actually asking. A sub-agent that
   * builds a workflow the parent then rewires leaves "Edited", the same answer
   * the chat would give if it had opened that workflow instead of making it.
   *
   * Safe to express as a precedence rather than a timestamp comparison: a
   * resource cannot be edited before it is created, so "edited if it was ever
   * edited" and "whichever came last" are the same answer, and this one does
   * not depend on message or merge ordering to get there.
   */
  private static latestArtifactOrigin(
    a: ArtifactOrigin | undefined,
    b: ArtifactOrigin | undefined
  ): ArtifactOrigin | undefined {
    const rank = (origin: ArtifactOrigin | undefined): number => {
      switch (origin) {
        case 'edited':
          return 2;
        case 'created':
          return 1;
        // Neither is a claim on the artifact, so neither can outrank one. They
        // are kept apart anyway: `opened` is a tool saying it changed nothing,
        // `undefined` is nobody having said.
        case 'opened':
        case undefined:
          return 0;
        default:
          return Assert.unreachable(origin);
      }
    };
    return rank(a) >= rank(b) ? a : b;
  }

  /**
   * What this chat's own tool calls did to each resource they touched.
   *
   * Two sources, because the tools fall into two groups.
   *
   * A tool that returns an artifact **states** what it did: `artifactResponse`
   * takes the verb as a required argument, so the answer is authored next to
   * the thing it describes and a new artifact tool cannot compile without
   * giving one. Nothing here keeps a list of which keys create.
   *
   * The rest are the mutators that return no artifact at all — a workflow is
   * opened once and then changed by node, edge and trigger calls whose output
   * is a plain result — so there is no envelope to read a statement off.
   * {@link ARTIFACT_EDITING_TOOL_KEYS} names those, and only those, and they
   * are matched on the resource id in their input. A read tool that also takes
   * a `workflowId` is not on the list and so cannot mark anything edited.
   *
   * Scoped to this conversation's messages. A sub-agent's calls are accounted
   * for in that sub-agent's own descriptors and arrive already stamped, which
   * is what keeps the union in {@link Conversation.latestArtifactOrigin}
   * meaningful.
   */
  private artifactOriginsByResourceId(): Map<string, ArtifactOrigin> {
    const origins = new Map<string, ArtifactOrigin>();

    for (const msgModel of this.messages) {
      for (const part of msgModel.parts) {
        if (!part.isToolPart()) {
          continue;
        }
        const artifact = part.getArtifact();
        if (artifact !== null) {
          const artifactId = artifact.artifactId;
          // `opened` is a statement too, and the statement is "no verb": it
          // records nothing and, crucially, does not overwrite an edit made to
          // the same workflow later in the chat.
          if (
            artifactId !== undefined &&
            artifactId !== '' &&
            artifact.origin !== undefined &&
            artifact.origin !== 'opened' &&
            // Never downgrades an edit already recorded against this id, so the
            // verb does not depend on `this.messages` being in wall-clock order
            // — see {@link Conversation.latestArtifactOrigin}.
            origins.get(artifactId) !== 'edited'
          ) {
            origins.set(artifactId, artifact.origin);
          }
          continue;
        }

        if (!coco.ARTIFACT_EDITING_TOOL_KEYS.has(part.toolKey().toString())) {
          continue;
        }
        for (const id of coco.artifactTargetIdsFromToolInput(part.input)) {
          origins.set(id, 'edited');
        }
      }
    }

    return origins;
  }

  /**
   * Artifact descriptors from tool message parts whose output uses the Cotera
   * artifact envelope (`ToolPart#getArtifact`), e.g. coco/edit-agent.
   * Scans every message’s hydrated parts (not only assistant `getToolParts()`),
   * so tool results are counted wherever they appear in the transcript.
   */
  private collectArtifactDescriptorsFromToolMessages(): ArtifactDescriptor[] {
    return this.collectTimestampedArtifactDescriptorsFromToolMessages().map(
      ({ descriptor }) => descriptor
    );
  }

  /**
   * As {@link Conversation.collectArtifactDescriptorsFromToolMessages}, paired with
   * the producing message’s `createdAt` so {@link Conversation.dismissedArtifactIds}
   * can tell a tool output that predates a dismissal from one that supersedes it.
   */
  private collectTimestampedArtifactDescriptorsFromToolMessages(): {
    descriptor: ArtifactDescriptor;
    createdAt: Date;
  }[] {
    const out: { descriptor: ArtifactDescriptor; createdAt: Date }[] = [];
    for (const msgModel of this.messages) {
      const messageId = msgModel.id ?? '';
      const toolParts = msgModel.parts.filter((p): p is ToolPart =>
        p.isToolPart()
      );
      for (const part of toolParts) {
        const artifact = part.getArtifact();
        if (artifact === null) {
          continue;
        }

        // Dedupe key for conversation.artifacts() is this field only (envelope id or toolCallId).
        const artifactId =
          artifact.artifactId !== undefined && artifact.artifactId !== ''
            ? artifact.artifactId
            : part.toolCallId;

        out.push({
          descriptor: {
            type: artifact.artifactType,
            artifactId,
            payload: artifact.data,
            messageId,
            toolCallId: part.toolCallId,
            toolName: part.toolKey().toString(),
          },
          createdAt: msgModel.createdAt,
        });
      }
    }
    return out;
  }

  /**
   * Artifact ids this chat has dismissed ({@link Conversation.removeArtifact})
   * and that nothing has re-added since.
   *
   * A dismissal is scoped in time, not permanent: an artifact re-opened by the
   * user or re-emitted by a later tool call comes back, which is what lets an
   * agent surface something the user had closed. Ordering compares the dismissal
   * against the `createdAt` of whatever produced the artifact.
   */
  private dismissedArtifactIds(): ReadonlySet<string> {
    const dismissedAt = new Map<string, number>();
    for (const action of this.actions) {
      if (!action.isRemoveArtifactAction() || !action.isCompleted()) {
        continue;
      }
      const artifactId = (action as RemoveArtifactAction).tryGetArtifactId();
      if (artifactId === null) {
        continue;
      }
      const at = action.actionData.createdAt.getTime();
      const previous = dismissedAt.get(artifactId);
      if (previous === undefined || at > previous) {
        dismissedAt.set(artifactId, at);
      }
    }

    if (dismissedAt.size === 0) {
      return new Set();
    }

    const reviveIfNewer = (artifactId: string, addedAt: Date): void => {
      const removedAt = dismissedAt.get(artifactId);
      if (removedAt !== undefined && addedAt.getTime() > removedAt) {
        dismissedAt.delete(artifactId);
      }
    };

    for (const action of this.actions) {
      if (!action.isLoadArtifactAction()) {
        continue;
      }
      const descriptor = (
        action as LoadArtifactAction
      ).tryGetArtifactDescriptor();
      if (descriptor === null) {
        continue;
      }
      reviveIfNewer(descriptor.artifactId, action.actionData.createdAt);
    }

    for (const {
      descriptor,
      createdAt,
    } of this.collectTimestampedArtifactDescriptorsFromToolMessages()) {
      reviveIfNewer(descriptor.artifactId, createdAt);
    }

    return new Set(dismissedAt.keys());
  }

  /**
   * Artifacts from this chat’s `load_artifact` actions and tool message outputs only
   * (no parent link, no descendant child merges). Used when hydrating a child with a
   * {@link ConversationParams.parent} so the parent’s loaded artifacts are visible without cycles.
   */
  ownChatArtifacts(): ArtifactDescriptor[] {
    return this.collectOwnChatArtifactDescriptors();
  }

  private static artifactDescriptorsSyncKey(
    descriptors: ArtifactDescriptor[]
  ): string {
    return descriptors
      .map(
        (descriptor) =>
          // `origin` is in the fingerprint because it is the one field that can
          // move while the id, type and payload all stand still: an edit tool
          // that emits no artifact changes what the chat did to a workflow
          // without changing the workflow descriptor at all. Leave it out and
          // the row keeps reporting the open until some unrelated artifact
          // happens to shift the key.
          `${descriptor.artifactId}\0${descriptor.type}\0${
            descriptor.origin ?? ''
          }\0${JSON.stringify(descriptor.payload)}`
      )
      .sort()
      .join('\0');
  }

  /**
   * Cheap synchronous fingerprint for artifact panel refresh. Changes when this chat's
   * own artifacts, linked parent's own artifacts (when resolved), any descendant's
   * artifacts, or an unresolved parent lazy may have changed; stable across unrelated
   * message updates (e.g. assistant text streaming).
   */
  artifactPanelSyncKey(): string {
    let parentKey = '';
    if (this.parentConversation !== undefined) {
      const parent = this.parentConversation.peek();
      if (parent !== undefined && parent !== null) {
        parentKey = Conversation.artifactDescriptorsSyncKey(
          parent.ownChatArtifacts()
        );
      } else if (!this.parentConversation.isLoaded()) {
        parentKey = '|p:pending|';
      }
    }
    const ownKey = Conversation.artifactDescriptorsSyncKey(
      this.ownChatArtifacts()
    );
    const childKeys = this.children
      .map((child) => child.artifactPanelSyncKey?.() ?? '')
      .sort()
      .join('\0');
    const parentPrefix = parentKey === '' ? '' : `${parentKey}|`;
    return `${parentPrefix}${ownKey}|c:${this.children.length}|${childKeys}`;
  }

  /**
   * Get all artifacts from the conversation.
   * Merges, in order (later wins on duplicate {@link ArtifactDescriptor.artifactId}):
   * 0. When {@link ConversationParams.parent} is set, awaits that lazy and merges the parent’s
   *    {@link Conversation.ownChatArtifacts} (parent `load_artifact` + parent tool outputs only)
   * 1. Programmatic `load_artifact` actions ({@link Conversation.addArtifact})
   * 2. Tool outputs in this chat’s messages (`ToolPart#getArtifact`)
   * 3. Each child’s `artifacts()` (messages, actions, and that child’s own descendants), so
   *    sub-agent artifacts override the parent for
   *    the same id and unique child-only ids are still included.
   *
   * Deduplication uses **only** each descriptor’s `artifactId` as the map key (never
   * ids inferred from `payload`). Ids this chat dismissed and nothing has re-added
   * since ({@link Conversation.dismissedArtifactIds}) are dropped from the result,
   * whichever of the four sources produced them.
   */
  async artifacts(): Promise<ArtifactDescriptor[]> {
    const byArtifactId = new Map<string, ArtifactDescriptor>();
    let parentConv: Conversation | null = null;
    if (this.parentConversation !== undefined) {
      parentConv = await this.parentConversation.get();
    }
    this.mergeParentOwnChatArtifactsInto(byArtifactId, parentConv);

    for (const d of this.collectOwnChatArtifactDescriptors()) {
      Conversation.mergeArtifactByArtifactId(byArtifactId, d);
    }

    for (const d of await this.collectDescendantArtifactDescriptors()) {
      Conversation.mergeArtifactByArtifactId(byArtifactId, d);
    }

    // Applied last so a dismissal also covers artifacts merged in from a parent
    // or a sub-agent, which `collectOwnChatArtifactDescriptors` never sees.
    const dismissed = this.dismissedArtifactIds();
    return Array.from(byArtifactId.values()).filter(
      (descriptor) => !dismissed.has(descriptor.artifactId)
    );
  }

  /**
   * {@link Conversation.ownChatArtifacts} backing merge (deduped by `artifactId`).
   */
  private collectOwnChatArtifactDescriptors(): ArtifactDescriptor[] {
    const byArtifactId = new Map<string, ArtifactDescriptor>();

    for (const action of this.actions) {
      if (!action.isLoadArtifactAction()) {
        continue;
      }

      const loadAction = action as LoadArtifactAction;
      const result = loadAction.tryGetArtifactDescriptor();
      if (result === null) {
        continue; // Skip incomplete data (e.g. from DB or stream)
      }

      const descriptor: ArtifactDescriptor = {
        type: result.type,
        artifactId: result.artifactId,
        payload: result.artifactData,
        messageId: '',
        toolCallId: result.toolCallId ?? loadAction.id,
        toolName: '',
      };

      Conversation.mergeArtifactByArtifactId(byArtifactId, descriptor);
    }

    for (const d of this.collectArtifactDescriptorsFromToolMessages()) {
      Conversation.mergeArtifactByArtifactId(byArtifactId, d);
    }

    // Stamped here rather than on each descriptor as it is built, because the
    // evidence is spread across the whole transcript and neither source above
    // can see all of it. A `load_artifact` descriptor knows no tool at all, and
    // a tool-output one knows only the call that emitted the envelope — which
    // for a workflow is the call that opened it, not the seven that changed it.
    const origins = this.artifactOriginsByResourceId();

    const dismissed = this.dismissedArtifactIds();
    return Array.from(byArtifactId.values())
      .filter((descriptor) => !dismissed.has(descriptor.artifactId))
      .map((descriptor) => {
        const origin = origins.get(descriptor.artifactId);
        return origin === undefined ? descriptor : { ...descriptor, origin };
      });
  }

  /**
   * Get messages that were modified (existed initially but were changed).   */
  getModifiedMessages(): MessageClass[] {
    return this.messages.filter(
      (msgModel) =>
        msgModel.id !== undefined && this.modifiedMessageIds.has(msgModel.id)
    );
  }

  /**
   * Get actions that were modified (existed initially but were changed).   */
  getModifiedActions(): ChatActionData[] {
    return this.actions
      .filter((action) => this.modifiedActionIds.has(action.id))
      .map((action) => action.actionData);
  }

  /**
   * Get actions that are new (didn't exist initially).   */
  getNewActions(): ChatActionData[] {
    return this.actions
      .filter((action) => this.newActionIds.has(action.id))
      .map((action) => action.actionData);
  }

  /**
   * Get messages that are new (didn't exist initially).   */
  getNewMessages(): MessageClass[] {
    return this.messages.filter(
      (msgModel) =>
        msgModel.id !== undefined && this.newMessageIds.has(msgModel.id)
    );
  }

  getRunKey(): string {
    return RunKeyHelpers.forConversation(this.conversationId);
  }

  /**
   * Get the conversation ID.   */
  getId(): string {
    return this.conversationId;
  }

  /**
   * Get the model used by this conversation.
   */
  getModel(): string {
    return coco.DEFAULT_MODEL;
  }

  /**
   * Get the org ID.
   */
  getOrgId(): string {
    return this.orgId;
  }

  /**
   * True when an organize-library draft row exists for this conversation (as
   * precomputed by the server turn handler). Lets the organize skill re-attach
   * its draft tools + instructions even if the chat's one-shot button seed was
   * lost, so Coco edits the staged plan instead of reading live folders.
   */
  hasOrganizeDraft(): boolean {
    return this.organizeDraftExists === true;
  }

  /**
   * Get the org that owns this conversation for credential/cross-org purposes.
   * Derived from resource/folder links when in a folder, otherwise chat orgId.
   */
  getOwnerOrgId(): string {
    return this.chatModel?.ownerOrgId ?? this.orgId;
  }

  /**
   * Get the folder ID when the conversation is placed in a folder (null otherwise).
   * Used by tools (e.g. create-agent) to place new agents in the same folder when no explicit folderId is passed.
   */
  getFolderId(): string | null {
    return this.chatModel?.folderId ?? null;
  }

  /**
   * Chat owner (`chats.actor_id` — who created the conversation).
   * For the user executing the current request, pass {@link getModelMessages}
   * `actorId` when that may differ (e.g. cross-org access).
   */
  getOwnerId(): string {
    return this.chatModel?.actorId ?? SYSTEM_USER_USER_ID;
  }

  /**
   * Actor for the current prompt build ({@link toLlmInput} `actorId`), or chat owner.
   */
  getPromptActorId(): string {
    return this.promptRenderOptions?.actorId ?? this.getOwnerId();
  }

  /** Whether default Coco tool keys count toward autoload skill gating (see `includeDefaults`). */
  getPromptIncludeDefaults(): boolean {
    return this.promptRenderOptions?.includeDefaults !== false;
  }

  getPromptInjectSkills(): boolean {
    if (this.promptRenderOptions?.injectSkills === false) {
      return false;
    }
    // A caller-supplied system prompt makes the chat clean-room: suppress the
    // autoloaded Coco registry skills so they don't reintroduce the Coco
    // persona / Cotera guidance on top of the override.
    return !this.hasCustomSystemPromptOverride();
  }

  /**
   * Document UUIDs referenced in system text, messages, linked agent, etc.
   */
  getDocumentReferenceIds(): string[] {
    return this.collectDocumentReferenceIds({
      suffix: this.skillsSuffix,
    });
  }

  /**
   * Full document reference set for the LLM prompt, including loaded agent system prompts.
   */
  async getDocumentReferenceIdsForPrompt(): Promise<string[]> {
    const loadedAgentContents = await this.getLoadedAgentContents();
    return this.collectDocumentReferenceIds({
      suffix: this.skillsSuffix,
      loadedAgentContents,
    });
  }

  /**
   * Hydrated agent instructions for loaded `coco/agent-*` tool keys.
   */
  async getLoadedAgentContents(
    actorId?: string
  ): Promise<LoadedAgentContent[]> {
    const loadedToolKeys = this.getLoadedTools().map((toolKey) =>
      toolKey.toString()
    );
    const agentToolKeys = loadedToolKeys.filter((key) => isAgentToolKey(key));
    if (agentToolKeys.length === 0 || this.agentLoader === undefined) {
      return [];
    }
    return this.agentLoader.loadAgents(
      agentToolKeys,
      this.orgId,
      actorId ?? this.getPromptActorId()
    );
  }

  /**
   * Trigger source that created this chat (defaults to chat for legacy rows).
   */
  triggeredBy(): ChatTriggerSource {
    return this.chatModel?.triggerSource ?? 'chat';
  }

  /**
   * Get the position of a specific message by its ID.
   * Returns undefined if the message ID is not found.   */
  getPositionForMessageId(messageId: string): number | undefined {
    return this.messagePositions.get(messageId);
  }

  /**
   * After persisting via the chat repository: align message positions with DB upserts
   * and clear dirty flags so callers need not reload from the database.
   */
  acknowledgePersistence(params: {
    messagePositions?: ReadonlyArray<{
      messageId: string;
      position: number;
    }>;
    actionIds?: ReadonlyArray<string>;
  }): void {
    for (const { messageId, position } of params.messagePositions ?? []) {
      this.messagePositions.set(messageId, position);
      this.newMessageIds.delete(messageId);
      this.modifiedMessageIds.delete(messageId);
    }
    for (const actionId of params.actionIds ?? []) {
      this.newActionIds.delete(actionId);
      this.modifiedActionIds.delete(actionId);
    }
  }

  private serializeRaw(): SerializedConversation {
    const messagePositions: Record<string, number> = {};
    for (const [msgId, pos] of this.messagePositions) {
      messagePositions[msgId] = pos;
    }
    const messageReadAt: Record<string, string | null> = {};
    for (const [msgId, readAt] of this.messageReadAt) {
      messageReadAt[msgId] = readAt !== null ? readAt.toISOString() : null;
    }
    const actions = this.actions.map((a) => {
      const d = a.actionData;
      return {
        id: d.id,
        actorId: d.actorId,
        chatId: d.chatId,
        type: d.type,
        status: d.status,
        data: d.data,
        completedAt:
          d.completedAt !== null ? d.completedAt.toISOString() : null,
        approvedAt: d.approvedAt !== null ? d.approvedAt.toISOString() : null,
        approvedBy: d.approvedBy,
        createdAt: d.createdAt.toISOString(),
        updatedAt: d.updatedAt.toISOString(),
      };
    });
    const chat = this.chatModel
      ? {
          id: this.chatModel.id,
          orgId: this.chatModel.orgId,
          actorId: this.chatModel.actorId,
          triggerSource: this.chatModel.triggerSource ?? null,
          agent: this.chatModel.agentIdentity,
          agentId: this.chatModel.agentId ?? null,
          title: this.chatModel.title,
          status: this.chatModel.status,
          parentId: this.chatModel.parentId ?? null,
          parentToolCallId: this.chatModel.getParentToolCallId(),
          folderId: this.chatModel.folderId ?? null,
          resourceId: this.chatModel.resourceId ?? null,
          ownerOrgId: this.chatModel.ownerOrgId,
          metadata: this.chatModel.metadata,
        }
      : undefined;
    const messages = this.messages.map((m) => {
      const readAt =
        m.id !== undefined ? this.messageReadAt.get(m.id) ?? null : null;
      return {
        uiMessage: m.uiMessage,
        type: m.type,
        readAt: readAt !== null ? readAt.toISOString() : null,
        createdAt: m.createdAt.toISOString(),
        updatedAt: m.updatedAt.toISOString(),
        status: m.status,
        ...(m.senderActorId !== undefined && { actorId: m.senderActorId }),
      };
    });
    const childSerialized =
      this.children.length > 0
        ? (this.children as Conversation[]).map((c) => c.serializeRaw())
        : [];
    const resolvedModel =
      this.conversationModel?.isLoaded() === true
        ? this.conversationModel.peek()
        : undefined;
    const unpersistedActionIds = Array.from(this.newActionIds);
    const serialized: SerializedConversation = {
      orgId: this.orgId,
      conversationId: this.conversationId,
      systemMessage: this.systemMessage,
      messages,
      chat,
      loadedTools: this.loadedTools.map((t) => t.toString()),
      messagePositions,
      actions,
      messageReadAt,
      ...(resolvedModel !== null &&
        resolvedModel !== undefined && {
          model: resolvedModel.serialize(),
        }),
      ...(childSerialized.length > 0 && { children: childSerialized }),
      ...(unpersistedActionIds.length > 0 && { unpersistedActionIds }),
    };
    // Normalize to plain JSON so callers can safely persist in durable journals.
    return JSON.parse(JSON.stringify(serialized)) as SerializedConversation;
  }

  /**
   * Serialize the conversation to a plain JSON-serializable object suitable for
   * persistence or transmission. Dates are converted to ISO strings.
   */
  serialize(): SerializedConversation {
    const normalized = normalizeToJsonSerializable(this.serializeRaw());
    Assert.assert(
      normalized !== undefined &&
        !Array.isArray(normalized) &&
        typeof normalized === 'object' &&
        normalized !== null,
      'Conversation.serialize() produced a non-object value'
    );
    return normalized as SerializedConversation;
  }

  /**
   * Fast equality for reconciliation: same message ids, message content (text + tool
   * input/output), and action id+status. Used to avoid unnecessary watchable updates
   * when reconciled snapshot is unchanged.
   */
  equals(other: Conversation | null): boolean {
    if (other === null || other === undefined) {
      return false;
    }
    const aMsgs = this.getAllMessages();
    const bMsgs = other.getAllMessages();
    if (aMsgs.length !== bMsgs.length) {
      return false;
    }
    for (let i = 0; i < aMsgs.length; i++) {
      const aMsg = aMsgs[i];
      const bMsg = bMsgs[i];
      const aId = aMsg?.id ?? '';
      const bId = bMsg?.id ?? '';
      if (aId !== bId) {
        return false;
      }
      if (aMsg?.contentSignature() !== bMsg?.contentSignature()) {
        return false;
      }
    }
    const aActions = this.getActions();
    const bActions = other.getActions();
    if (aActions.length !== bActions.length) {
      return false;
    }
    for (let i = 0; i < aActions.length; i++) {
      const a = aActions[i];
      const b = bActions[i];
      if (a === undefined || b === undefined) {
        return false;
      }
      if (a.id !== b.id || a.status !== b.status) {
        return false;
      }
    }
    if (this.children.length !== other.children.length) {
      return false;
    }

    return true;
  }

  /**
   * Get the current position of the conversation.
   * Returns the position of the last message, or -1 if there are no messages.   */
  position(): number {
    if (this.messages.length === 0) {
      return -1;
    }

    // Get the maximum position from messagePositions map
    const positions = Array.from(this.messagePositions.values());
    if (positions.length === 0) {
      return -1;
    }

    return Math.max(...positions);
  }

  /**
   * Create a clone of this conversation.
   * Returns a new Conversation instance with the same state.   */
  clone(
    overrides?: Partial<
      Pick<
        ConversationParams,
        | 'agentLoader'
        | 'skillLoader'
        | 'skillsSuffix'
        | 'learnedFactsLoader'
        | 'toolReceiptsLoader'
        | 'ingotSchemaLoader'
        | 'organizeDraftExists'
      >
    >
  ): Conversation {
    return Conversation.createFromParams({
      orgId: this.orgId,
      conversationId: this.conversationId,
      systemMessage: this.systemMessage,
      messages: this.messages.map((msgModel) => ({
        uiMessage: msgModel.uiMessage,
        type: msgModel.type,
        createdAt: msgModel.createdAt,
        ...(msgModel.senderActorId !== undefined && {
          actorId: msgModel.senderActorId,
        }),
      })),
      ...(this.chatModel !== undefined && { chat: this.chatModel.chatData }),
      ...(this.linkedAgent !== undefined && { linkedAgent: this.linkedAgent }),
      loadedTools: [...this.loadedTools],
      messagePositions: new Map(this.messagePositions),
      actions: this.actions.map((actionModel) => actionModel.actionData),
      toolLoader: this.toolLoader,
      agentLoader: overrides?.agentLoader ?? this.agentLoader,
      skillLoader: overrides?.skillLoader ?? this.skillLoader,
      workspaceDomainLoader: this.workspaceDomainLoader,
      cocoToolKeys: this.cocoToolKeys,
      children: this.children.map((child) =>
        child instanceof Conversation ? child.clone() : child
      ),
      ...(this.parentConversation !== undefined && {
        parent: new Lazy(async () => {
          const resolved = await this.parentConversation!.get();
          if (resolved === null) {
            return null;
          }
          return resolved.clone();
        }),
      }),
      ...(this.conversationModel !== undefined && {
        model: new Lazy(async () => {
          const resolved = await this.conversationModel!.get();
          if (resolved === null) {
            return null;
          }
          return new ChatModel(resolved.serialize());
        }),
      }),
      ...(this.chatSentimentLog !== undefined && {
        sentimentLog: new Lazy<readonly ChatSentimentLogRecord[]>(async () => [
          ...(await this.chatSentimentLog!.get()),
        ]),
      }),
      ...(this.referencedDocumentsById !== undefined && {
        referencedDocumentsById: this.referencedDocumentsById,
      }),
      skillsSuffix: overrides?.skillsSuffix ?? this.skillsSuffix,
      learnedFactsLoader:
        overrides?.learnedFactsLoader ?? this.learnedFactsLoader,
      toolReceiptsLoader:
        overrides?.toolReceiptsLoader ?? this.toolReceiptsLoader,
      ingotSchemaLoader: overrides?.ingotSchemaLoader ?? this.ingotSchemaLoader,
      organizeDraftExists:
        overrides?.organizeDraftExists ?? this.organizeDraftExists,
    });
  }

  /**
   * Returns true if the given message has at least one incomplete tool call.
   * Uses the same isIncomplete() logic as canPassToLLM. Use this for per-message checks.
   */
  hasIncompleteToolCallsInMessage(messageId: string): boolean {
    const msg = this.getMessageById(messageId);
    if (msg === undefined || !msg.isAssistantMessage()) {
      return false;
    }
    const toolParts = msg.getToolParts();
    return toolParts.some((p) => p.isIncomplete());
  }

  /**
   * Check if the conversation is ready to be passed back to the LLM.
   * Returns false if there are any pending tool calls (input-available or input-streaming state).
   * If there is a newer user message after an assistant message with incomplete tool parts,
   * the conversation can yield so the assistant can respond to the latest user turn.
   * Returns false if the newest visible assistant message has no parts (failed stream placeholder
   * or still materializing); that row must not be followed by another step-chat on a broken tail.
   * An empty row with a later assistant row after it is an abandoned shell, not in-flight work, so
   * it does not block.
   * Returns true if all tool calls are complete (output-available, output-error, or output-denied).
   */
  shouldYieldControlBackToTheLlm(): boolean {
    const { hasEmptyBlockingAssistant, incompleteToolParts } =
      this.getLlmYieldBlockers();
    return (
      hasEmptyBlockingAssistant === false && incompleteToolParts.length === 0
    );
  }

  /**
   * True when control should return to the client rather than continue server-side
   * orchestration.
   *
   * Complements {@link shouldYieldControlBackToTheLlm} (not a strict boolean inverse):
   * - Waiting on ad-hoc `client/*` interruptable tools → true (client must run them),
   *   even though the LLM gate is false.
   * - Current visible assistant stream finished → true. Workflows, sub-agents, and other
   *   server-side incomplete tools keep the LLM gate false until they resolve, so those
   *   waits do not look "finished" here. A streaming (non-finalized) assistant also
   *   returns false even when tool parts are otherwise clear.
   */
  shouldYieldBackToClient(): boolean {
    const { hasEmptyBlockingAssistant, incompleteToolParts } =
      this.getLlmYieldBlockers();

    if (hasEmptyBlockingAssistant) {
      return false;
    }

    if (incompleteToolParts.length > 0) {
      return incompleteToolParts.every((part) =>
        part.toolKey().toString().startsWith(INTERRUPTABLE_TOOL_KEY_PREFIX)
      );
    }

    return this.latestVisibleAssistantIsFinalized();
  }

  /**
   * Visible assistants that currently block yielding to the LLM (same rules as
   * {@link shouldYieldControlBackToTheLlm}): not hidden, not superseded by a newer
   * user message, and either empty parts or incomplete tool parts.
   */
  private getLlmYieldBlockers(): {
    hasEmptyBlockingAssistant: boolean;
    incompleteToolParts: ToolPart[];
  } {
    let newestUserPosition: number | undefined;
    for (const [index, message] of this.messages.entries()) {
      if (!message.isUserMessage()) {
        continue;
      }
      const position = this.stableMessagePosition(message, index);
      if (newestUserPosition === undefined || position > newestUserPosition) {
        newestUserPosition = position;
      }
    }

    const latestAssistant = this.latestVisibleAssistant();

    let hasEmptyBlockingAssistant = false;
    const incompleteToolParts: ToolPart[] = [];

    for (const [index, msgWithType] of this.messages.entries()) {
      if (!msgWithType.isAssistantMessage()) {
        continue;
      }
      // Hidden rows are orchestration-only; they must not block parent completion when
      // visible chat messages are already terminal (nested sub-agent race).
      if (msgWithType.isHidden()) {
        continue;
      }

      const assistantPosition = this.stableMessagePosition(msgWithType, index);
      if (
        newestUserPosition !== undefined &&
        newestUserPosition > assistantPosition
      ) {
        continue;
      }

      const parts = msgWithType.raw().parts ?? [];
      if (parts.length === 0) {
        // An empty visible assistant is a stream shell, and only blocks while it is
        // still the newest one. Once a later assistant row exists the writer has moved
        // on and nothing will ever append here, so treating it as in-flight strands the
        // run: the completed lifecycle event is suppressed and never re-emitted.
        // Deliberately scoped to this branch — a later assistant says nothing about
        // whether the incomplete tool parts below (sub-agents, workflows) have resolved.
        const supersededByLaterAssistant =
          latestAssistant !== undefined &&
          (latestAssistant.position > assistantPosition ||
            (latestAssistant.position === assistantPosition &&
              latestAssistant.index > index));
        if (!supersededByLaterAssistant) {
          hasEmptyBlockingAssistant = true;
        }
        continue;
      }

      for (const toolPart of msgWithType.getToolParts()) {
        if (toolPart.isIncomplete()) {
          incompleteToolParts.push(toolPart);
        }
      }
    }

    return { hasEmptyBlockingAssistant, incompleteToolParts };
  }

  /**
   * Newest visible (non-hidden) assistant row, ordered by {@link stableMessagePosition}
   * with the array index as tie-break. Undefined when there is no visible assistant.
   */
  private latestVisibleAssistant():
    | { message: MessageClass; position: number; index: number }
    | undefined {
    let latest:
      | { message: MessageClass; position: number; index: number }
      | undefined;
    for (const [index, message] of this.messages.entries()) {
      if (!message.isAssistantMessage() || message.isHidden()) {
        continue;
      }
      const position = this.stableMessagePosition(message, index);
      if (
        latest === undefined ||
        position > latest.position ||
        (position === latest.position && index > latest.index)
      ) {
        latest = { message, position, index };
      }
    }
    return latest;
  }

  /**
   * Latest visible assistant (by {@link stableMessagePosition}) is terminal: not
   * streaming, has parts, and no incomplete tool parts.
   */
  private latestVisibleAssistantIsFinalized(): boolean {
    const latest = this.latestVisibleAssistant();
    if (latest === undefined) {
      return false;
    }
    return this.isFinalizedVisibleAssistantMessage(latest.message);
  }

  /**
   * True when the chronologically last non-compaction message is a visible user message and
   * the latest visible assistant message (by {@link stableMessagePosition}) is finalized:
   * not streaming, has parts, and no incomplete tool parts — i.e. a complete assistant turn
   * followed by a user message at the tail (typically awaiting the next model step).
   *
   * Note: self-heal runs at construction; superseded streaming or incomplete assistant rows after
   * a newer visible user message are finalized first, so this reflects that normalized state.
   */
  isUserTurn(): boolean {
    type Ranked = { message: MessageClass; position: number; index: number };

    const rank = (message: MessageClass, index: number): Ranked => ({
      message,
      position: this.stableMessagePosition(message, index),
      index,
    });

    const isAfter = (a: Ranked, b: Ranked): boolean =>
      a.position > b.position ||
      (a.position === b.position && a.index > b.index);

    let trailing: Ranked | undefined;
    let latestAssistant: Ranked | undefined;

    for (const [index, message] of this.messages.entries()) {
      if (message.isCompactionMessage()) {
        continue;
      }
      const r = rank(message, index);
      if (trailing === undefined || isAfter(r, trailing)) {
        trailing = r;
      }
      if (message.isAssistantMessage() && !message.isHidden()) {
        if (latestAssistant === undefined || isAfter(r, latestAssistant)) {
          latestAssistant = r;
        }
      }
    }

    if (trailing === undefined) {
      return false;
    }
    if (!trailing.message.isUserMessage() || trailing.message.isHidden()) {
      return false;
    }
    if (latestAssistant === undefined) {
      return false;
    }
    return this.isFinalizedVisibleAssistantMessage(latestAssistant.message);
  }

  /**
   * Check if there are approved tool calls that need to be executed.
   * Returns true if there are any pending tool calls that have been approved.
   */
  async hasApprovedToolCallsToExecute(): Promise<boolean> {
    for (const msgWithType of this.messages) {
      if (!msgWithType.isAssistantMessage()) {
        continue;
      }

      const toolParts = msgWithType.getToolParts();
      for (const toolPart of toolParts) {
        if (await this.shouldReexecuteToolPart(toolPart)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Find the assistant message containing approved tool calls that need execution.
   * Returns the message and its position, or undefined if not found.
   */
  async findMessageWithApprovedToolCalls(): Promise<
    | {
        message: MessageClass;
        position: number;
      }
    | undefined
  > {
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }

      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }

      const position = this.messagePositions.get(msgId);
      if (position === undefined) {
        continue;
      }

      // Use domain model method to get tool parts
      const toolParts = msgModel.getToolParts();
      for (const toolPart of toolParts) {
        if (await this.shouldReexecuteToolPart(toolPart)) {
          return { message: msgModel, position };
        }
      }
    }

    return undefined;
  }

  /**
   * Find the assistant message containing incomplete tool calls that are ready to execute.
   * Unlike findMessageWithApprovedToolCalls(), this does not require an approval action.
   * Used when resuming after runInterruptedTool (user filled in tool input; no approval flow).
   */
  findMessageWithIncompleteToolCalls():
    | {
        message: MessageClass;
        position: number;
      }
    | undefined {
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }

      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }

      const position = this.messagePositions.get(msgId);
      if (position === undefined) {
        continue;
      }

      const toolParts = msgModel.getToolParts();
      for (const toolPart of toolParts) {
        if (toolPart.isIncomplete()) {
          return { message: msgModel, position };
        }
      }
    }

    return undefined;
  }

  /**
   * Returns true when there are tool calls with input but no output yet (input-available,
   * input-streaming, or undefined state). Distinct from hasIncompleteToolCallsInMessage:
   * tools that returned incomplete({}) have output and are excluded — they are waiting
   * for user input, not automatic re-execution.
   */
  hasUnExecutedToolCalls(): boolean {
    for (const msgWithType of this.messages) {
      if (!msgWithType.isAssistantMessage()) {
        continue;
      }
      const hasUnExecuted = msgWithType
        .getToolParts()
        .some((p) => p.needsExecution());
      if (hasUnExecuted) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find the first assistant message with tool calls that need execution (input-available
   * or input-streaming, no output). Used for the replay-artifact path where writeToolResults
   * was skipped — excludes tools that returned incomplete({}) (waiting for user).
   */
  findMessageWithUnExecutedToolCalls():
    | {
        message: MessageClass;
        position: number;
      }
    | undefined {
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }

      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }

      const position = this.messagePositions.get(msgId);
      if (position === undefined) {
        continue;
      }

      const toolParts = msgModel.getToolParts();
      for (const toolPart of toolParts) {
        if (toolPart.needsExecution()) {
          return { message: msgModel, position };
        }
      }
    }

    return undefined;
  }

  /**
   * Find the first assistant message with incomplete tool calls at a position greater than minPosition.
   * Used when resuming to process the next pending tool. Handles non-sequential positions (e.g. gaps
   * from compaction) - using resumePosition+1 would skip the actual message if position is in a gap.
   */
  findFirstMessageWithIncompleteToolCallsAfterPosition(minPosition: number):
    | {
        message: MessageClass;
        position: number;
      }
    | undefined {
    const candidates: Array<{ message: MessageClass; position: number }> = [];
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }
      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }
      const position = this.messagePositions.get(msgId);
      if (position === undefined || position <= minPosition) {
        continue;
      }
      const toolParts = msgModel.getToolParts();
      if (toolParts.some((p) => p.isIncomplete())) {
        candidates.push({ message: msgModel, position });
      }
    }
    if (candidates.length === 0) {
      return undefined;
    }
    candidates.sort((a, b) => a.position - b.position);
    return candidates[0];
  }

  /**
   * Get the message at the given position if it is an assistant message with incomplete tool calls.
   * Used when resuming after runInterruptedTool to process the exact message at resumePosition + 1
   * (the assistant message that contains the tool call we are resuming).
   */
  getMessageAtPositionIfIncompleteToolCalls(position: number):
    | {
        message: MessageClass;
        position: number;
      }
    | undefined {
    const msgModel = this.getMessageAtPosition(position);
    if (msgModel === undefined || !msgModel.isAssistantMessage()) {
      return undefined;
    }

    const msgId = msgModel.id;
    if (msgId === undefined) {
      return undefined;
    }

    const pos = this.messagePositions.get(msgId);
    if (pos === undefined) {
      return undefined;
    }

    const toolParts = msgModel.getToolParts();
    const hasIncomplete = toolParts.some((p) => p.isIncomplete());
    if (!hasIncomplete) {
      return undefined;
    }

    return { message: msgModel, position: pos };
  }

  /**
   * Get all assistant message IDs that have incomplete tool calls, ordered by position.
   * Used for deterministic multi-message tool processing (e.g. reasoning models that
   * produce multiple assistant messages per step). The order is stable for durable replay.
   */
  getIncompleteMessageIdsOrderedByPosition(): string[] {
    const withPosition: Array<{ messageId: string; position: number }> = [];
    for (const msgModel of this.messages) {
      if (msgModel === undefined || !msgModel.isAssistantMessage()) {
        continue;
      }
      const msgId = msgModel.id;
      if (msgId === undefined) {
        continue;
      }
      const position = this.messagePositions.get(msgId);
      if (position === undefined) {
        continue;
      }
      const toolParts = msgModel.getToolParts();
      const hasIncomplete = toolParts.some((p) => p.isIncomplete());
      if (hasIncomplete) {
        withPosition.push({ messageId: msgId, position });
      }
    }
    withPosition.sort((a, b) => a.position - b.position);
    return withPosition.map((x) => x.messageId);
  }

  /**
   * Infer loaded tools from the conversation history.
   * Extracts tool keys from all tool call parts in the messages.
   * Also checks for coco/load-resource (and legacy coco/load-tool) calls to extract nested tool keys.
   */
  private inferLoadedToolsFromHistory(): ToolKey[] {
    const toolKeys = new Set<string>();

    for (const msgModel of this.messages) {
      const messageToolKeys = msgModel.getToolKeys(this.cocoToolKeys);
      for (const toolKey of messageToolKeys) {
        toolKeys.add(toolKey.toString());
      }
    }

    const graph = this.buildExplorerForReferencedDocuments();
    if (graph !== null) {
      for (const keyStr of this.inferToolKeysFromNestedDocumentMentions(
        graph
      )) {
        toolKeys.add(keyStr);
      }
    }

    return Array.from(toolKeys).map((key) => ToolKey.fromString(key));
  }

  private buildExplorerForReferencedDocuments(): BuildExplorer | null {
    const map = this.referencedDocumentsById;
    if (map === undefined || map.size === 0) {
      return null;
    }
    const graph: Record<string, Resolution> = {};
    let opIndex = 0;
    for (const [docId, source] of map) {
      const identity = Identity.forOperation(0, opIndex);
      opIndex += 1;
      const documentNode: DocumentNode = {
        t: 'document',
        source,
        meta: {
          identity,
          isLocked: false,
          editsAt: [],
        },
      };
      graph[docId] = {
        item: { t: 'document', documentNode },
        deps: {},
      };
    }
    return BuildExplorer.fromGraph(graph);
  }

  /**
   * Tool keys from @mentions inside editor-state parts, including mentions in nested
   * workspace documents when {@link #buildExplorerForReferencedDocuments} is non-null.
   */
  private inferToolKeysFromNestedDocumentMentions(
    graph: BuildExplorer
  ): string[] {
    const out = new Set<string>();

    const recordMentionTools = (
      state: DocumentState,
      visitedDocumentIds: Set<string>
    ): void => {
      try {
        const editor = new EditorState(state);
        for (const m of editor.getMentions(graph)) {
          if (m.type === 'tool') {
            const toolKeyStr = m.referenceId ?? m.id ?? m.label;
            if (
              typeof toolKeyStr === 'string' &&
              toolKeyStr.trim().length > 0
            ) {
              try {
                out.add(ToolKey.fromString(toolKeyStr.trim()).toString());
              } catch {
                // ignore invalid tool keys
              }
            }
          }
          if (m.type === 'agent') {
            const agentId = m.referenceId ?? m.id;
            if (typeof agentId === 'string' && agentId.trim().length > 0) {
              out.add(agentIdToAgentToolKey(agentId.trim()));
            }
          }
        }
      } catch {
        // invalid editor JSON
      }

      for (const ref of collectWorkspaceResourceRefs(state)) {
        if (ref.resourceType === ResourceType.Agent) {
          out.add(agentIdToAgentToolKey(ref.resourceId));
        }
        if (ref.resourceType === ResourceType.Document) {
          if (visitedDocumentIds.has(ref.resourceId)) {
            continue;
          }
          visitedDocumentIds.add(ref.resourceId);
          const nested = this.referencedDocumentsById?.get(ref.resourceId);
          if (nested !== undefined) {
            recordMentionTools(nested, visitedDocumentIds);
          }
        }
      }
    };

    for (const msgModel of this.messages) {
      const parts = msgModel.uiMessage.parts ?? [];
      for (const part of parts) {
        const p = part as { type?: string; editorState?: unknown };
        if (
          p.type !== 'editor-state' ||
          p.editorState === undefined ||
          p.editorState === null
        ) {
          continue;
        }
        recordMentionTools(p.editorState as DocumentState, new Set<string>());
      }
    }

    return [...out];
  }

  /**
   * Infer loaded tools from actions (add_tool, focus_agent, load_resource for
   * agents and workflows).
   * - add_tool (approved/completed): toolKeyToAdd
   * - focus_agent (completed): agent tool key derived from agentId (coco/agent-xxx)
   * - load_resource (completed, type agent): same agent tool key as focus_agent
   * - load_resource (completed, type workflow): workflow tool key derived from
   *   the workflow id (coco/workflow-xxx)
   *
   * This is the only thing keeping a dynamically loaded agent/workflow tool
   * alive across turns: `loadTool` is in-memory, `save()` persists only
   * messages and actions, and ATC re-reads the conversation on every step.
   */
  private inferLoadedToolsFromActions(): ToolKey[] {
    const toolKeys = new Set<string>();

    for (const action of this.actions) {
      if (
        action.isAddToolAction() &&
        (action.isApproved() || action.isCompleted())
      ) {
        const addAction = action as AddToolAction;
        try {
          toolKeys.add(addAction.getToolKey());
        } catch {
          // Invalid or missing toolKeyToAdd, skip
        }
      }
      if (action.isFocusAgentAction() && action.isCompleted()) {
        const focusAction = action as FocusAgentAction;
        try {
          toolKeys.add(agentIdToAgentToolKey(focusAction.getAgentId()));
        } catch {
          // Invalid or missing agentId, skip
        }
      }
      if (action.isLoadResourceAction() && action.isCompleted()) {
        const loadAction = action as LoadResourceAction;
        try {
          if (loadAction.getResourceType() === 'agent') {
            toolKeys.add(agentIdToAgentToolKey(loadAction.getResourceId()));
          }
          if (loadAction.getResourceType() === 'workflow') {
            toolKeys.add(
              workflowIdToWorkflowToolKey(loadAction.getResourceId())
            );
          }
        } catch {
          // Invalid load_resource payload, skip
        }
      }
    }

    return Array.from(toolKeys).map((key) => ToolKey.fromString(key));
  }

  /**
   * Infer tools that were loaded dynamically after conversation start.
   * These should not be treated as part of the initial "included tools" set.
   */
  private inferDynamicallyLoadedToolKeys(): Set<string> {
    const dynamicToolKeys = new Set<string>();

    for (const action of this.actions) {
      if (
        action.isAddToolAction() &&
        (action.isApproved() || action.isCompleted())
      ) {
        const addAction = action as AddToolAction;
        try {
          dynamicToolKeys.add(
            ToolKey.fromString(addAction.getToolKey()).toString()
          );
        } catch {
          // skip invalid tool key
        }
      }
    }

    for (const msgModel of this.messages) {
      const dynamicallyLoadedByMessage = msgModel.getDynamicallyLoadedToolKeys(
        this.cocoToolKeys
      );
      for (const toolKey of dynamicallyLoadedByMessage) {
        dynamicToolKeys.add(toolKey.toString());
      }
    }

    return dynamicToolKeys;
  }

  /**
   * Filter out tools that were removed after their last usage.
   * For each tool, finds when it was last used in messages, then checks if there's
   * a remove_tool action that comes after that last usage.
   */
  private filterRemovedTools(baseTools: Set<string>): ToolKey[] {
    // Find the last usage position for each tool
    const toolLastUsagePosition = new Map<string, number>();

    // Scan messages in order to find last usage of each tool
    for (let i = 0; i < this.messages.length; i++) {
      const msgModel = this.messages[i];
      if (!msgModel) {
        continue;
      }

      const messageToolKeys = msgModel.getToolKeys(this.cocoToolKeys);
      for (const toolKey of messageToolKeys) {
        const toolKeyStr = toolKey.toString();
        // Update last usage position (higher index = later usage)
        toolLastUsagePosition.set(toolKeyStr, i);
      }
    }

    // Completed remove_tool actions plus unload_resource(agent) as equivalent removals
    const removeToolActions = this.actions
      .filter((action) => action.isRemoveToolAction() && action.isCompleted())
      .sort(
        (a, b) =>
          a.actionData.createdAt.getTime() - b.actionData.createdAt.getTime()
      ) as RemoveToolAction[];

    const hasRemovalForTool = (toolKeyStr: string): boolean => {
      for (const action of removeToolActions) {
        if (action.getToolKey() === toolKeyStr) {
          return true;
        }
      }
      for (const action of this.actions) {
        if (!action.isUnloadResourceAction() || !action.isCompleted()) {
          continue;
        }
        const u = action as UnloadResourceAction;
        try {
          if (
            u.getResourceType() === 'agent' &&
            agentIdToAgentToolKey(u.getResourceId()) === toolKeyStr
          ) {
            return true;
          }
        } catch {
          // skip invalid unload_resource
        }
      }
      return false;
    };

    // Filter out tools that were removed after their last usage
    const filteredToolKeys = new Set<string>();

    for (const toolKeyStr of baseTools) {
      const lastUsagePosition = toolLastUsagePosition.get(toolKeyStr);

      // If tool was never used, check if it was removed
      if (lastUsagePosition === undefined) {
        // Any remove_tool or unload_resource(agent) for this tool excludes it
        if (!hasRemovalForTool(toolKeyStr)) {
          filteredToolKeys.add(toolKeyStr);
        }
        continue;
      }

      const removeAction =
        removeToolActions.filter((a) => a.getToolKey() === toolKeyStr).pop() ??
        undefined;

      if (!removeAction && !hasRemovalForTool(toolKeyStr)) {
        // No remove action, include the tool
        filteredToolKeys.add(toolKeyStr);
      } else {
        // Check if remove action comes after last usage.
        // Since we don't have exact message timestamps, we use message position as a proxy:
        // if there are messages after the last usage, the remove likely happened after usage.
        const messagesAfterLastUsage =
          this.messages.length - 1 - lastUsagePosition;

        if (messagesAfterLastUsage > 0) {
          // There are messages after last usage, so remove likely happened after usage
          continue;
        } else {
          // Last usage is at the end, so we can't determine if remove happened after
          // Include the tool to be safe
          filteredToolKeys.add(toolKeyStr);
        }
      }
    }

    return Array.from(filteredToolKeys).map((key) => ToolKey.fromString(key));
  }

  /**
   * Build map of assistant message ID to read timestamp from messages.
   */
  private buildMessageReadAtMap(messages: Message[]): Map<string, Date | null> {
    const map = new Map<string, Date | null>();
    for (const msg of messages) {
      if (
        msg.uiMessage.id !== undefined &&
        msg.uiMessage.role === 'assistant'
      ) {
        map.set(msg.uiMessage.id, msg.readAt ?? null);
      }
    }
    return map;
  }

  /**
   * Check if the conversation has any unread assistant messages.
   */
  hasUnreadMessages(): boolean {
    for (const [, readAt] of this.messageReadAt) {
      if (readAt === null) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get IDs of assistant messages that are unread.
   */
  getUnreadAssistantMessageIds(): string[] {
    const ids: string[] = [];
    for (const [msgId, readAt] of this.messageReadAt) {
      if (readAt === null) {
        ids.push(msgId);
      }
    }
    return ids;
  }

  /**
   * Get the message ID of the first unread assistant message (by position).
   * Returns null if all assistant messages are read.
   */
  getFirstUnreadMessageId(): string | null {
    let firstId: string | null = null;
    let minPosition = Infinity;
    for (const [msgId, readAt] of this.messageReadAt) {
      if (readAt !== null) {
        continue;
      }
      const pos = this.messagePositions.get(msgId);
      if (pos !== undefined && pos < minPosition) {
        minPosition = pos;
        firstId = msgId;
      }
    }
    return firstId;
  }

  /**
   * Mark a message as modified if it existed initially.   */
  private markMessageAsModified(messageId: string | undefined): void {
    if (messageId === undefined) {
      return;
    }

    if (this.initialMessageIds.has(messageId)) {
      this.modifiedMessageIds.add(messageId);
      this.newMessageIds.delete(messageId);
    }
  }

  /**
   * Mark an action as modified if it existed initially.
   */
  private markActionAsModified(actionId: string): void {
    if (this.initialActionIds.has(actionId)) {
      this.modifiedActionIds.add(actionId);
      this.newActionIds.delete(actionId);
    }
  }

  /**
   * Active skills whose instructions + bundled tools should inject into this
   * run. Interactive `chat` gets every active skill; other triggers (workflow,
   * slack, cron, …) get only skills flagged {@link ResolvedSkill.triggerIndependent}
   * — their relevance comes from the prompt / agent definition, not the chat
   * context (e.g. `coco/document-references`, so an agent that references a
   * document can read it on any trigger). An explicit `injectSkills: false`
   * (extract-object runs) suppresses everything.
   */
  protected async resolveSkillsToInject(): Promise<readonly ResolvedSkill[]> {
    if (this.skillLoader === undefined || !this.getPromptInjectSkills()) {
      return [];
    }
    const activeSkillKeys = await this.getActiveSkillKeys();
    if (activeSkillKeys.length === 0) {
      return [];
    }
    const resolved = await this.skillLoader.loadSkills(activeSkillKeys, this);
    if (
      shouldInjectRegistrySkillsForPrompt({
        trigger: this.triggeredBy(),
        injectSkills: this.getPromptInjectSkills(),
      })
    ) {
      return resolved;
    }
    return resolved.filter((skill) => skill.triggerIndependent);
  }

  /**
   * True when any message in this conversation `@`-mentions a dataset.
   *
   * The system-message path ({@link Message.getMentionedToolKeysAsync}) already
   * turns an agent's or document's dataset mention into a tool key, but a dataset
   * mentioned in the *chat box* lives on a user message, which that path never
   * reads. This covers that half.
   */
  private mentionsDataset(): boolean {
    for (const state of collectEditorStateRootsFromMessages(this.messages)) {
      for (const ref of collectWorkspaceResourceRefs(state)) {
        if (ref.resourceType === ResourceType.Dataset) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Merge tool keys bundled by loaded developer skills into the runtime allowlist.
   * Always strips the linked agent's own `coco/agent-…` key so every trigger
   * (chat, workflow, slack, API, …) refuses self-delegation.
   *
   * A `@`-mentioned dataset also brings `coco/dataset-read` in here, at the one
   * point every {@link Conversation.getToolKeys} implementation passes through —
   * the mention compiles to a name and a column list, and without the read tool
   * there is no way to get from that to a row.
   */
  protected async appendSkillBundledToolKeys(
    keys: readonly string[]
  ): Promise<readonly string[]> {
    // Every tool result and attached document lands in the chat's ingot, so
    // the query tool that reads it back is always offered.
    const withIngotQuery = appendUniqueToolKeys(
      [...keys],
      [COCO_INGOT_QUERY_TOOL_KEY]
    );
    const withDatasetRead = this.mentionsDataset()
      ? appendUniqueToolKeys(withIngotQuery, [COCO_DATASET_READ_TOOL_KEY])
      : withIngotQuery;
    const skillToolKeys = (await this.resolveSkillsToInject()).flatMap(
      (skill) => skill.toolKeys
    );
    return this.withoutLinkedAgentSelfToolKey(
      appendUniqueToolKeys(withDatasetRead, skillToolKeys)
    );
  }
}

/** Root Coco chat (`trigger=chat`, no linked saved agent). */
export class CocoChatConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const withRequiredTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_WEB_SEARCH_TOOL_KEY,
        COCO_SITE_SCRAPER_TOOL_KEY,
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const keys = appendUniqueToolKeys(
      withRequiredTools,
      explicitlyIncludedToolKeys
    );
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Chat linked to a saved agent (`trigger=chat`, `agentId` / hydrated {@link Agent}). */
export class AgentChatConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const withRequiredTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const keys = appendUniqueToolKeys(
      withRequiredTools,
      explicitlyIncludedToolKeys
    );
    return this.withoutLinkedAgentSelfTool(
      await this.appendSkillBundledToolKeys(keys)
    );
  }
}

/** Slack thread with a linked saved agent. */
export class SlackAgentChatConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const withRequiredTools = this.getLoadedTools().map((toolKey) =>
      toolKey.toString()
    );

    const keys = appendUniqueToolKeys(
      withRequiredTools,
      explicitlyIncludedToolKeys
    );
    return this.withoutLinkedAgentSelfTool(
      await this.appendSkillBundledToolKeys(keys)
    );
  }
}

/** Nested sub-agent thread (`chats.parent_id` set). */
export class SubAgentConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const keys = this.getLoadedTools().map((toolKey) => toolKey.toString());
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });

    const merged = appendUniqueToolKeys(
      keys.filter((x) => !this.getExcludedToolKeys().includes(x)),
      explicitlyIncludedToolKeys
    );
    return this.withoutLinkedAgentSelfTool(
      await this.appendSkillBundledToolKeys(merged)
    );
  }
}

/** Chat-bot integration thread (Slack, Linear, etc.) without a linked saved agent. */
export class ChatBotConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const withInteractiveTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(
      withInteractiveTools,
      explicitlyIncludedToolKeys
    );
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Chat-bot integration thread with a linked saved agent. */
export class ChatBotAgentChatConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const withRequiredTools = appendUniqueToolKeys(
      [],
      this.getLoadedTools().map((toolKey) => toolKey.toString())
    );
    const keys = appendUniqueToolKeys(
      withRequiredTools,
      explicitlyIncludedToolKeys
    );
    return this.withoutLinkedAgentSelfTool(
      await this.appendSkillBundledToolKeys(keys)
    );
  }
}

/** Slack channel thread without a linked saved agent. */
export class SlackConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const withInteractiveTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(
      withInteractiveTools,
      explicitlyIncludedToolKeys
    );
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Linear agent session without a linked saved agent (legacy trigger). */
export class LinearConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const withInteractiveTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(
      withInteractiveTools,
      explicitlyIncludedToolKeys
    );
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Linear session with a linked saved agent (legacy trigger). */
export class LinearAgentChatConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const withRequiredTools = appendUniqueToolKeys(
      [],
      [
        ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
        COCO_SEARCH_TOOL_KEY,
        COCO_LOAD_RESOURCE_TOOL_KEY,
      ]
    );
    const keys = appendUniqueToolKeys(
      withRequiredTools,
      explicitlyIncludedToolKeys
    );
    return this.withoutLinkedAgentSelfTool(
      await this.appendSkillBundledToolKeys(keys)
    );
  }
}

/** Cron trigger conversations. */
export class CronConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys([], explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Webhook trigger conversations. */
export class WebhookConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const resolved = [
      ...this.getLoadedTools().map((toolKey) => toolKey.toString()),
    ];
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(resolved, explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/**
 * Dataset trigger conversations (a row changed on a watched dataset).
 *
 * Same non-interactive shape as {@link EventConversation}: nobody is at the
 * keyboard, so only tools the system message explicitly mentions are loaded.
 */
export class DatasetConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys([], explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Event trigger conversations (emit-event listeners). */
export class EventConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys([], explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/**
 * Warehouse / graph-column runs ({@link AtcRunTrigger.Warehouse}, e.g. `llm.jobs`
 * `completion`). Tools are seeded via `initialToolKeys` → load actions
 * ({@link Conversation.seedPersistedLoadedTools}), then resolved here with
 * non-interactive filtering (no default Coco toolkit).
 */
export class WarehouseConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const loadedTools = this.getLoadedTools().map((toolKey) =>
      toolKey.toString()
    );
    const keys = appendUniqueToolKeys(loadedTools, explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Workflow trigger conversations. */
export class WorkflowConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const resolved = this.getLoadedTools().map((toolKey) => toolKey.toString());
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(resolved, explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** System trigger conversations. */
export class SystemConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    const resolved = this.getLoadedTools().map((toolKey) => toolKey.toString());
    const systemMessage = this.resolveLlmSystemMessage();
    const explicitlyIncludedToolKeys =
      await systemMessage.getMentionedToolKeysAsync({
        workspaceDomainLoader: this.getWorkspaceDomainLoader(),
      });
    const keys = appendUniqueToolKeys(resolved, explicitlyIncludedToolKeys);
    return this.appendSkillBundledToolKeys(keys);
  }
}

/** Compaction-summary runs expose no tools to the LLM. */
export class CompactionSummaryConversation extends Conversation {
  override async getToolKeys(): Promise<readonly string[]> {
    return [];
  }
}

/** @deprecated Use {@link Conversation.createFromParams}. */
export const createConversation = Conversation.createFromParams;

// Compile-time check: Conversation satisfies SerializableClass (has fromSerialized).
const _conversationSerializableCheck: SerializableClass<
  Conversation,
  SerializedConversation
> = Conversation;
void _conversationSerializableCheck;
